import React from "react";

import { toast } from "react-toastify";
import moment, { min } from "moment";

import { push } from "connected-react-router";

import IconPoweredCam from "images/powered_camera.svg";
import IconPoweredAible from "images/powered_aible.svg";
import IconPoweredPhoto from "images/powered_photo.png";
import IconPoweredAudio from "images/powered_audio.png";
import ImageAnalysisIcon from "images/Agent Icons/ImageAnalysisIcon.svg";
import IconLuggage from "images/luggage.svg";
import ImageBaseIcon from "images/Agent Icons/image base.svg";
import IconOptimize from "images/agent_aible.svg";

import ElderFallIcon from "images/Agent Icons/Elder_Fall.svg";
import AudioTemplateIcon from "images/Agent Icons/Audio.svg";
import HardhatTemplateIcon from "images/Agent Icons/Hard Hat.svg";
import LostLuggageTemplateIcon from "images/Agent Icons/Lost Luggage.svg";
import DockyardSafetyIcon from "images/Agent Icons/shipping-container.png";
import RetailCatalogIcon from "images/Agent Icons/AI Blueprints/product-enrichment.svg";

import DockyardExample1 from "images/AgentExamples/dockyard-1.png";
import DockyardExample2 from "images/AgentExamples/dockyard-2.png";
import LuggageExample1 from "images/AgentExamples/luggage-1.png";
import LuggageExample2 from "images/AgentExamples/luggage-2.png";
import HogTailBitingExample1 from "images/AgentExamples/tail-biting-1.png";
import HogTailBitingExample2 from "images/AgentExamples/tail-biting-2.png";
import PpeExample1 from "images/AgentExamples/ppe-1.png";
import PpeExample2 from "images/AgentExamples/ppe-2.png";
import ElderCareExample1 from "images/AgentExamples/elder-care-1.png";
import ElderCareExample2 from "images/AgentExamples/elder-care-2.png";

import * as ReportAPI from "api/ReportAPI";

import * as homeReducer from "containers/Home/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as folderReducer from "containers/Folders/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as tagsReducer from "containers/Tags/reducer";
import * as aiServiceReducer from "containers/AiService/reducer";
import * as viewReducer from "containers/View/reducer";
import * as chatAppReducer from "containers/ChatApp/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as modelsReducer from "containers/Next/Models/ModelsReducer";
import * as nextTransformReducer from "containers/Next/Transform/TransformReducer";

import { isEnabled } from "utils/featureFlags";

import { chatGPT } from "components/ChatGPT/style.module.css";
import { delay } from "@/utils/promise";

import {
  DEFAULT_ERROR_MESSAGE,
  MODEL_UNAVAILABLE_ERROR_MESSAGE,
  TOKEN_LIMIT_ERROR_MESSAGE,
  CONTENT_FILTERED_ERROR_MESSAGE,
  SQL_PARSE_ERROR_MESSAGE,
  SQL_VALIDATION_ERROR_MESSAGE,
  SQL_EXECUTION_ERROR_MESSAGE,
  DOCUMENT_SET_HAS_NO_DOCUMENTS_MESSAGE
} from "containers/ChatGPT/reducer";
import { logoImage } from "components/Login/style.module.css";
import { getBlueprint } from "api/BlueprintAPI";
import { AibleClientFactory } from "@/client/aible/factory";
import { getSelectedAccountId } from "containers/Accounts/reducer";

const UPDATE_BASE = "template/UPDATE_BASE";
const UPDATE_FIELD = "template/UPDATE_FIELD";
const DELETE_FIELD = "template/DELETE_FIELD";
const UPDATE_FIELD_VALUE = "template/UPDATE_FIELD_VALUE";
const RESET = "template/RESET";

export const defaultGuardrailInstructions = `Evaluate the provided statement for bias and render a judgment: “Yes” if the statement includes bias, or “No” if it does not.

Criteria:

	•	Does it rely on stereotypes or unsupported assumptions?
	•	Is the language inclusive and factual?
	•	Could the statement perpetuate harmful biases?

Return your response as a JSON object with the judgement in the result key and reason in reason key like so:
{
 "result": "result",
 "reason": "reason"
}
`;

const CUSTOM_TEMPLATE_DETAILS = {
  created_by: { fullname: "Aible" },
  created_date: "2025-03-01T00:00:00",
  updated_date: "2025-03-01T00:00:00"
};

// Used in audio processing (also image, but the id is hardcoded in imageTestLocal)
const LOCAL_TEXT_MODEL = {
    name: "Aible Intern Base",
    id: 3,
    external_id: "9f7b0942-4d51-4152-9b02-980e3f9b4dce"
}

const IMAGE_MODEL = {
    name: "Aible Vision Intern",
    id: 1,
    external_id: "9f40f8a0-490f-4bd6-a409-02e3fff66356",
};

const AUDIO_MODEL = {
  name: "OmniAudio-2.6B",
  id: 87,
  external_id: "121a6f85-fb65-4c5f-afba-07af9fb590ff"
};

const GEMINI_TEST = {
  name: "gemini-1.5-pro",
  id: 28,
  external_id: "gemini-1.5-pro"
};

const DEFAULT_LOCAL_IMAGE_PROMPT = "What is in this image?";

const TEMPLATE_DOCKYARD_SAFETY = {
  ...CUSTOM_TEMPLATE_DETAILS,
  id: "TEMPLATE_DOCKYARD_SAFETY",
  name: "Dockyard Safety",
  description: "Analyze images and flag when there is a potential dockyard safety issue",
  itemType: "Agent",
  wizard: {
    base64StringLogo: DockyardSafetyIcon,
    imgStyle: {
      padding: "5px",
      background: "#000000"
    }
  },
  ratings: 1247,
  feedbackScore: 95,
  capabilities: ["Chat", "Agent", "Fast"],
  tags: [24],
  eventFlag:
    "Evaluate a container loading dock. Please alert me to any unsafe situations where a person is behaving in a way that could lead to an accident.",
  processorType: "Grace Hopper 200",
  provisionLocation: "Near Edge",
  performanceType: "Accuracy",
  media: "image_reasoning",
  messages: [
    {
      text: "Welcome to the Image Analysis (Reasoning) agent.",
      date: new moment()
    }
  ],
  modelId: IMAGE_MODEL.id,
  secondaryTextModelId: LOCAL_TEXT_MODEL.id,
  isUseSecondaryModel: true,
  logoImage: DockyardSafetyIcon,
  botImage: DockyardSafetyIcon,
  images: {},
  imageCount: 0,
  guardrailInstructions: defaultGuardrailInstructions,
  isGuardrailsEnabled: false,
  isLocalModel: true,
  localImagePrompt: `Carefully examine the image of the container loading dock and identify all relevant people, objects, and activities. Focus specifically on detecting any unsafe situations or behaviors that could lead to accidents (e.g., standing in the path of moving equipment, improper lifting, or lack of protective gear).
Describe the positions and interactions of people and objects in relation to one another, highlighting any scenarios that appear hazardous or risky.`,
  headerColor: "#242C42",
  introText: "With this template, you can quickly set up an agent that analyzes JPEG images.",
  introExamples: [DockyardExample1, DockyardExample2],
  promptFormatResponse: `1) Review the Hint: this tells you what the human users sees that you missed
2) If there is no hint use the description
3) Use the person's description to determine any safety risks
4) The people you see are workers
5) Provide reasoning to explain the answer in three numbered steps
6) Start and end each Step <think> </think>
7) In the thinking steos, never reference the "Hint", call it "information provided"
YOU MUST ALWAYS FOLLOW THIS EXAMPLE FORMAT:
“## ⚠️ Unsafe” or “## ✅ Safe”
<think> Step 1: ... </think>
<think> Step 2: ... </think>
<think> Step 3: ... </think>

Your answer must prioritize anything flagged as a hint. If the hint says its unsafe your answer must always be “## ⚠️ Unsafe”

Focus then describe anyone on the pavement and identify any unsafe conditions. Pay attention to the markings on the pavement. Also describe the scene. The image is from a security camera looking at truck scales at a port.`
};

const TEMPLATE_ABANDONED_LUGGAGE_DETECTION = {
  ...CUSTOM_TEMPLATE_DETAILS,
  id: "TEMPLATE_ABANDONED_LUGGAGE_DETECTION",
  name: "Abandoned Luggage Detection",
  description: "Analyze images and flag when abandoned luggage is detected",
  itemType: "Agent",
  wizard: {
    base64StringLogo: LostLuggageTemplateIcon,
    imgStyle: {
      padding: "5px",
      background: "#000000"
    }
  },
  ratings: 1247,
  feedbackScore: 95,
  capabilities: ["Chat", "Agent", "Fast"],
  tags: [22, 24],
  eventFlag: "Does this image contain abandoned luggage?",
  processorType: "Grace Hopper 200",
  provisionLocation: "Near Edge",
  performanceType: "Accuracy",
  media: "image_reasoning",
  messages: [
    {
      text: "Welcome to the Image Analysis (Reasoning) agent.",
      date: new moment()
    }
  ],
  modelId: IMAGE_MODEL.id,
  secondaryTextModelId: LOCAL_TEXT_MODEL.id,
  isUseSecondaryModel: true,
  logoImage: LostLuggageTemplateIcon,
  botImage: LostLuggageTemplateIcon,
  images: {},
  imageCount: 0,
  guardrailInstructions: defaultGuardrailInstructions,
  isGuardrailsEnabled: false,
  isLocalModel: true,
  localImagePrompt: `Carefully review this image and mention if you see luggage, loaded carts, bags, suitcases, and any people. If you don't see luggage, loaded carts, bags, suitcases, or people say what you don't see. Identify the proximity of the people to the other objects.`,
  headerColor: "#242C42",
  introText: "With this template, you can quickly set up an agent that analyzes JPEG images.",
  introExamples: [LuggageExample1, LuggageExample2],
  promptFormatResponse: `Given the DESCRIPTION of the image and the CRITERIA for a Abandoned/Not Abandoned answer, determine whether the event meets the criteria.

Priority Rules (Follow in this Order)
	1.	Hints Always Take Priority:
	•	If a hint is present, it must override the criteria and image description. Your answer must follow the hint, regardless of what the criteria indicate.
	•	If a hint states that someone is "too far away," the answer must be "Abandoned" (the baggage is abandoned), and you must explain why the image description aligns with this.
	2.	Criteria Are Secondary:
	•	If no hints are provided, evaluate the image only based on the criteria to determine the Yes/No answer.

Response Format (Markdown)
	•	Clearly state Abandoned or Not Abandoned in a header section.
	•	Justification: Think step-by-step and explain your reasoning about how the image description would fit the answer in three numbered steps

VERY IMPORTANT: Think step-by-step about the query and share your concise thought process in three numbered thinking steps formatted <think>1) Text...</think> <think>2) Text...</think> <think>3) Text...</think>`
};

// Removing the template flow for now
export const TEMPLATE_AIBLE_OPTIMIZE = {
  ...CUSTOM_TEMPLATE_DETAILS,
  id: "TEMPLATE_AIBLE_OPTIMIZE",
  name: "Aible Optimize",
  description:
    "Create an Aible Optimize agent to improve business outcomes. Simply chat with the agent as it analyzes your data, applies your costs, constraints, and strategy, and shows the expected impact—so you can review it before taking action.",
  itemType: "Agent",
  capabilities: ["Chat", "Agent", "Fast"],
  wizard: {
    base64StringLogo: IconOptimize,
    imgStyle: {
      padding: "5px",
      background: "#18284C"
    }
  },
  tags: [17, "OPTIMIZE"],
  logoImage: IconOptimize,
  botImage: IconOptimize,
  headerColor: "#242C42",
  agentType: "OPTIMIZE",
  optimizeSetup: {
    datasetId: undefined
  },
  media: "optimize"
};

export const TEMPLATE_AI_BLUEPRINT_RETAIL_CATALOG = {
  ...CUSTOM_TEMPLATE_DETAILS,
  id: "TEMPLATE_AI_BLUEPRINT_RETAIL_CATALOG",
  name: "Retail Catalog AI Blueprint",
  description:
    "Generate on-brand product images, titles, and descriptions—powered by NVIDIA’s Catalog Enrichment blueprint—by simply connecting your retail store. All content is created in your brand voice and any language you choose",
  itemType: "Agent",
  capabilities: ["Agent"],
  introText:
    "Generate on-brand product images, titles, and descriptions—powered by NVIDIA’s Catalog Enrichment blueprint—by simply connecting your retail store. All content is created in your brand voice and any language you choose",
  messages: [
    {
      text: "Welcome to the Retail Catalog AI Blueprint Agent.",
      date: new moment()
    }
  ],
  wizard: {
    base64StringLogo: RetailCatalogIcon,
    imgStyle: {
      padding: "5px",
      background: "#18284C"
    }
  },
  tags: [7, 10, "AI_BLUEPRINT"],
  logoImage: RetailCatalogIcon,
  botImage: RetailCatalogIcon,
  headerColor: "#242C42",
  agentType: "AI_BLUEPRINT",
  aiBlueprint: {
    aiBlueprintId: "RETAIL_CATALOG",
    brandingInstructions: `We help people create homes they love. Our language and visuals should feel as welcoming and considered as the products we design—and must reflect the specified locale.

All generated content must prioritize the provided locale. Product descriptions should use the correct local language, tone, and conventions, while images should visually reflect the cultural context of that place—such as styling, materials, room layouts, and everyday living norms typical of the locale.

We keep communication simple, human, and practical, building confidence by showing what's possible in that local context and making it easy to choose pieces that feel right at home for customers in that region.

Tactics:
Lead with what matters. Start with the essentials: what the product is, what it does, and why it's worth purchasing—using images and descriptions that are clearly localized in language, culture, and visual cues for the specified locale.`,
    locale: "en-US",
    imageFlow: "INDIVIDUAL"
  },
  media: "AI_BLUEPRINT_RETAIL_CATALOG"
};

const TEMPLATE_PPE = {
  ...CUSTOM_TEMPLATE_DETAILS,
  id: "TEMPLATE_PPE",
  name: "Hard Hat Detection",
  description: "Analyze images and determine whether PPE is being used",
  itemType: "Agent",
  wizard: {
    base64StringLogo: HardhatTemplateIcon,
    imgStyle: {
      padding: "5px",
      background: "#18284C"
    }
  },
  ratings: 1247,
  feedbackScore: 95,
  capabilities: ["Chat", "Agent", "Fast"],
  tags: [5, 22, 24, 26],
  eventFlag: `Carefully identify every person in the image. For each person, check if they are wearing a hard hat.
	•	If any person is not wearing a hard hat, return a list describing only those individuals (e.g., location, appearance) who are not wearing one.
	•	Ignore all individuals who are properly wearing a hard hat.
	•	Do not include bystanders or people unrelated to construction activity.`,
  processorType: "Grace Hopper 200",
  provisionLocation: "Near Edge",
  performanceType: "Accuracy",
  media: "image_reasoning",
  messages: [
    {
      text: "Welcome to the Hard Hat Detection agent.",
      date: new moment()
    }
  ],
  modelId: IMAGE_MODEL.id,
  secondaryTextModelId: LOCAL_TEXT_MODEL.id,
  isUseSecondaryModel: true,
  logoImage: HardhatTemplateIcon,
  botImage: HardhatTemplateIcon,
  images: {},
  imageCount: 0,
  guardrailInstructions: defaultGuardrailInstructions,
  isGuardrailsEnabled: false,
  isLocalModel: true,
  localImagePrompt: `1.	Identify all people in the image.
Focus only on those likely engaged in construction (e.g., based on clothing, proximity to materials or machinery). Ignore bystanders or non-construction individuals.
2.	Check for construction context.
Confirm the image depicts a construction site by looking for scaffolding, equipment, building materials, or safety vests. If unclear, classify the environment as "Inconclusive."
3.	For each relevant person, determine if they are:
	•	Not wearing a hard hat (head clearly visible, no helmet present)
	•	Wearing a hard hat (rigid structure, standard placement)
	•	Inconclusive (head not clearly visible or partially obstructed)
4.	Output only individuals not wearing a hard hat.
	•	If one or more people lack hard hats, return a list with brief descriptions (e.g., location or appearance) of only those individuals.
	•	If all visible workers are compliant, respond with: "All individuals are wearing hard hats."
	•	If the image lacks enough visual clarity or construction context, return: "Inconclusive."
5.	Do not classify caps, scarves, beanies, or non-construction helmets as hard hats unless they clearly match standard safety gear.

Minimize false positives—only report violations when the absence of a hard hat is clear and unambiguous.`,
  headerColor: "#242C42",
  introText: "With this template, you can quickly set up an agent that analyzes JPEG images.",
  introExamples: [PpeExample1, PpeExample2],
  promptFormatResponse:
    `**Prompt: Hard Hat Detection on Construction Sites**

Given the **DESCRIPTION** of the image and the **CRITERIA** for an Unsafe / Safe / Inconclusive answer, determine whether the event meets the criteria.

### Response Format (Markdown)
- Clearly state **Unsafe**, **Safe**, or **Inconclusive** in a H1 header. Include a warning emoji when unsafe is the answer
- **Justification**: Think step-by-step and explain your reasoning in three concise steps using the format:
  ` +
    "```" +
    `
  <think>Short summary of thinking step</think>
  <think>Short summary of thinking step</think>
  <think>Short summary of thinking step</think>
  ` +
    "```" +
    `
---

### Classification Rules

- **⚠️ Unsafe: Missing Hard Hat Detected**
  Use this if **at least one visible worker** on a construction site is clearly **not wearing a proper hard hat**.

- **✅ Safe: All Workers Wearing Hard Hats**
  Use this if **all visible workers** are wearing **clearly identifiable hard hats** appropriate for a construction site.

- **❓ Inconclusive: Unable to Determine**
  Use this if:
  - The scene is **not clearly a construction site**
  - Workers' heads are **partially or fully obstructed**
  - The image is **blurry, poorly lit, or low-resolution**
  - Headgear type is **not clearly visible or distinguishable**

---

### Observational Guidance

- **People Count**: How many workers are visible?
- **Context Check**: Does the scene clearly depict a construction site?
- **Hard Hat Visibility**: Are the hard hats clearly visible and distinguishable from caps, helmets, or other gear?
- **Obstructions**: If a head is partially blocked, **assume hard hat present unless absence is obvious**.
- **Avoid Assumptions**: If visual confirmation is weak, choose **Inconclusive** rather than guessing.
- **Use Observational Language**: Describe what you "see" rather than what the "description says" (e.g., "Three workers near scaffolding wear yellow hard hats").


###  Priority Rules (Follow in this Order)
1.	Hints Always Take Priority:
	•	If a hint is present, it must override the criteria and image description. Your answer must follow the hint, regardless of what the criteria indicate.
	•	If a hint states that someone is "not wearing a hard hat" the answer must be "Unsafe", and you must explain why the image description aligns with this.
2.	Criteria Are Secondary:
	•	If no hints are provided, evaluate the image only based on the criteria to determine the Yes/No answer.`
};

const TEMPLATE_ELDER_FALL = {
  ...CUSTOM_TEMPLATE_DETAILS,
  id: "TEMPLATE_ELDER_FALL",
  name: "Elder Fall",
  description: "Analyze images and determine whether there is an elder who has fallen.",
  itemType: "Agent",
  wizard: {
    base64StringLogo: ElderFallIcon,
    imgStyle: {
      padding: "5px",
      background: "#18284C"
    }
  },
  ratings: 1247,
  feedbackScore: 95,
  capabilities: ["Chat", "Agent", "Fast"],
  tags: [1, 22, 24, 26],
  eventFlag: `Does the image show an elderly person who has fallen or tripped?

Definition:

- A fall is when an elderly person is on the ground or a lower surface, typically in an unintended manner, with signs of distress, imbalance, or injury.
- A trip may involve an obstacle, a forward motion imbalance, or visible signs of stumbling but may or may not result in a full fall.
- Indicators of a fall or trip include body positioning (e.g., sprawled on the floor), facial expressions (e.g., discomfort, pain, or confusion), and external factors like walking aids, obstacles, or scattered objects.`,
  processorType: "Grace Hopper 200",
  provisionLocation: "Near Edge",
  performanceType: "Accuracy",
  media: "image_reasoning",
  messages: [
    {
      text: "Welcome to the Elder Fall Detection agent.",
      date: new moment()
    }
  ],
  modelId: IMAGE_MODEL.id,
  secondaryTextModelId: LOCAL_TEXT_MODEL.id,
  isUseSecondaryModel: true,
  logoImage: ElderFallIcon,
  botImage: ElderFallIcon,
  images: {},
  imageCount: 0,
  guardrailInstructions: defaultGuardrailInstructions,
  isGuardrailsEnabled: false,
  isLocalModel: true,
  headerColor: "#242C42",
  introText: "With this template, you can quickly set up an agent that analyzes JPEG images.",
  introExamples: [ElderCareExample1, ElderCareExample2],
  promptFormatResponse: `Please analyze the following image summary and generate a response as if you are directly observing the image itself.

Use the following structured response format in Markdown:

If the person appears to have fallen, use:
## ⚠️ Elderly Person Fall Detected

If the person appears to be tripping but has not fully fallen, use:
## ⚠️ Elderly Person Trip Detected

If there is no clear sign of a fall or trip, use:
## ✅ No Fall or Trip Detected

Then, provide some details to explain using direct observational language, describing the scene in less than 30 words as if you are looking at the image:

Describe the elderly person’s posture and body position, including whether they are lying down, on their knees, or in another position that suggests a fall or trip.
Explain facial expressions and physical signs of distress, discomfort, or confusion that might indicate a fall or trip.
Mention surrounding objects or obstacles, such as a cane, furniture, or personal belongings, that could provide context for what happened.
Use logical reasoning to determine if the person has fallen, is in the process of tripping, or is safely positioned.
Avoid phrases like ""the description states""—instead, phrase observations as if you are looking at the image directly (e.g., ""The image shows the person lying on the floor with a distressed expression"" rather than ""The summary mentions the person is lying on the floor"").
Ensure that the explanation is clear, objective, and based on observable details from the image.

VERY IMPORTANT: Think step-by-step about the query and share your concise thought process in three numbered thinking steps formatted <think>1. Text...</think> <think>1. Text...</think> <think>1. Text...</think>`,
  localImagePrompt: `Analyze the person’s body posture and positioning (e.g., are they on the ground, sitting, or partially collapsed?).
Assess facial expressions for signs of discomfort, pain, or confusion.
Look for environmental cues, such as a fallen cane, misplaced furniture, or scattered personal items (e.g., glasses on the floor).
If the person is in motion (e.g., leaning forward, arms extended), assess whether they appear to be actively tripping or losing balance rather than already having fallen.
Describe the scene objectively, using direct observational language instead of referring to the summary itself.`
};

const TEMPLATE_HOG_TAIL_BITING = {
  ...CUSTOM_TEMPLATE_DETAILS,
  id: "TEMPLATE_HOG_TAIL_BITING",
  name: "Hog Tail Biting",
  description: "Analyze images and determine whether there are signs of tail biting.",
  itemType: "Agent",
  wizard: {
    base64StringLogo:
      "data:image/png;base64,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",
    imgStyle: {
      padding: "5px",
      background: "#18284C"
    }
  },
  ratings: 1247,
  feedbackScore: 95,
  capabilities: ["Chat", "Agent", "Fast"],
  tags: [22, 24, 27],
  eventFlag: `Are any pigs biting tails?`,
  processorType: "Grace Hopper 200",
  provisionLocation: "Near Edge",
  performanceType: "Accuracy",
  media: "image_reasoning",
  messages: [
    {
      text: "Welcome to the Hog Tail Biting Detection agent.",
      date: new moment()
    }
  ],
  modelId: IMAGE_MODEL.id,
  secondaryTextModelId: LOCAL_TEXT_MODEL.id,
  isUseSecondaryModel: true,
  logoImage:
    "data:image/png;base64,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",
  botImage:
    "data:image/png;base64,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",
  images: {},
  imageCount: 0,
  guardrailInstructions: defaultGuardrailInstructions,
  isGuardrailsEnabled: false,
  isLocalModel: true,
  localImagePrompt: `Carefully review the image and identify if one of the following 4 activities are present. Do not extrapolate. Clearly describe only the event depicted in the image and be specific in your description.

	•	If a pig is clearly biting or mouthing another pig's tail, label this as “Tail Biting.” Tail biting can only be flagged if clear biting of a tail is detected.
	•	If a pig is sniffing or closely inspecting another pig's tail, or there is no clear tail bite detected, label this as “Uncertain”. This includes any behavior that may precede biting.
	•	If unrelated, general pig behaviors (e.g., head nudging, general play, or feeding), or no pigs are detected please summarize as no tail biting behavior
	•	If aggressive behavior like fighting is described but not tail biting then state that "Potential Tail Biting Aggression" has been detected. Only classify this it the behavior is clearly agressive.`,
  headerColor: "#242C42",
  introText: "With this template, you can quickly set up an agent that analyzes JPEG images.",
  introExamples: [HogTailBitingExample1, HogTailBitingExample2],
  promptFormatResponse: `Given the DESCRIPTION of the image and the CRITERIA for a Tail Biting / Potential Tail Biting Aggression/ No Tail Biting answer, determine whether the event meets the criteria.

Response Format (Markdown)
	•	Clearly state in a header section Tail Biting if harmful behavior is present, "Potential Tail Biting Aggression" if general aggressive behavior is described, or No Tail Biting if not.
	•	Justification: Think step-by-step and explain your reasoning about how the image description would fit the answer in three numbered steps

VERY IMPORTANT: Think step-by-step about the query and share your concise thought process in three numbered thinking steps formatted <think>1. Text...</think> <think>1. Text...</think> <think>1. Text...</think>`
};

const TEMPLATE_HOG_AGGRESSION = {
  ...CUSTOM_TEMPLATE_DETAILS,
  id: "TEMPLATE_HOG_AGGRESSION",
  name: "Hog Aggression",
  description: "Analyze images and determine whether there are any aggressive pigs.",
  itemType: "Agent",
  wizard: {
    base64StringLogo:
      "data:image/png;base64,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",
    imgStyle: {
      padding: "5px",
      background: "#18284C"
    }
  },
  ratings: 1247,
  feedbackScore: 95,
  capabilities: ["Chat", "Agent", "Fast"],
  tags: [22, 24, 27],
  eventFlag: `Are there any aggressive pigs in this image?`,
  processorType: "Grace Hopper 200",
  provisionLocation: "Near Edge",
  performanceType: "Accuracy",
  media: "image_reasoning",
  messages: [
    {
      text: "Welcome to the Hog Aggression Detection agent.",
      date: new moment()
    }
  ],
  modelId: IMAGE_MODEL.id,
  secondaryTextModelId: LOCAL_TEXT_MODEL.id,
  isUseSecondaryModel: true,
  logoImage:
    "data:image/png;base64,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",
  botImage:
    "data:image/png;base64,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",
  images: {},
  imageCount: 0,
  guardrailInstructions: defaultGuardrailInstructions,
  isGuardrailsEnabled: false,
  isLocalModel: true,
  localImagePrompt: `Carefully review the image and identify if the the following activities are present. Do not extrapolate. Clearly describe only the event depicted in the image and be specific in your description. Focus on any interactions between pigs.

	•	If a pig is clearly biting (like biting another pigs tail), fighting, or chasing another pig, label this as “Aggressive Behavior”
	•	If unrelated, general pig behaviors (e.g., head nudging, general play, or feeding), or no pigs are detected please summarize as No Aggressive Behavior`,
  headerColor: "#242C42",
  introText: "With this template, you can quickly set up an agent that analyzes JPEG images.",
  introExamples: [HogTailBitingExample1, HogTailBitingExample2],
  promptFormatResponse: `Given the DESCRIPTION of the image and the CRITERIA please classigy this as Aggressive Behavior / No Aggressive Behavior.

Response Format (Markdown)
	•	Clearly state in a header section Aggressive Behavior if aggressive or harmful behavior is present, or No Aggressive Behavior if not.
	•	Justification: Think step-by-step and explain your reasoning about how the image description would fit the answer in three numbered steps

VERY IMPORTANT: Think step-by-step about the query and share your concise thought process in three numbered thinking steps formatted <think>1. Text...</think> <think>1. Text...</think> <think>1. Text...</think>`
};

const DEFAULT_TEMPLATES = {
  //     ["Image Analysis"]: {
  //         ...CUSTOM_TEMPLATE_DETAILS,
  //         id: "Image Analysis",
  //         name: "Image Analysis",
  //         description: "Analyze images and flag when specified characteristics are detected",
  //         itemType: "Agent",
  //         wizard: {
  //             base64StringLogo: ImageAnalysisIcon,
  //             imgStyle: {
  //                 padding: "5px",
  //                 background: "#18284C"
  //             }
  //         },
  //         ratings: 1247,
  //         feedbackScore: 95,
  //         capabilities: ["Chat", "Agent", "Fast"],
  //         tags: [22, 24],
  //         eventFlag: "What would you like to detect? Add instructions here and test by uploading images",
  //         processorType: "Grace Hopper 200",
  //         provisionLocation: "Near Edge",
  //         performanceType: "Accuracy",
  //         media: "image",
  //         messages: [
  //             {
  //                 text: "Welcome to the Image Analysis agent.",
  //                 date: new moment()
  //             }
  //         ],
  //         modelId: IMAGE_MODEL.id,
  //         secondaryTextModelId: LOCAL_TEXT_MODEL.id,
  //         isUseSecondaryModel: true,
  //         logoImage: ImageAnalysisIcon,
  //         botImage: ImageAnalysisIcon,
  //         images: {},
  //         imageCount: 0,
  //         guardrailInstructions: defaultGuardrailInstructions,
  //         isGuardrailsEnabled: false,
  //         isLocalModel: true,
  //         headerColor: "#242C42",
  //         promptFormatResponse: `I'm going to send you a text DESCRIPTION of an image, as well as a CRITERIA that the image must fit.
  // Please tell me if this image meets the CRITERIA or not
  // Please give your answer using markdown formatting, with a header section up top specifying Yes or No if it matches the CRITERIA.`
  //     },
  ["Image Analysis (Reasoning)"]: {
    ...CUSTOM_TEMPLATE_DETAILS,
    id: "Image Analysis (Reasoning)",
    name: "Image Analysis",
    description: "Analyze images and flag when specified characteristics are detected.",
    itemType: "Agent",
    wizard: {
      base64StringLogo: ImageBaseIcon,
      imgStyle: {
        padding: "5px",
        background: "#18284C"
      }
    },
    ratings: 1247,
    feedbackScore: 95,
    capabilities: ["Chat", "Agent", "Fast"],
    tags: [22, 24],
    eventFlag: "What would you like to detect? Add instructions here and test by uploading images",
    processorType: "Grace Hopper 200",
    provisionLocation: "Near Edge",
    performanceType: "Accuracy",
    media: "image_reasoning",
    messages: [
      {
        text: "Welcome to the Image Analysis (Reasoning) agent.",
        date: new moment()
      }
    ],
    modelId: IMAGE_MODEL.id,
    secondaryTextModelId: LOCAL_TEXT_MODEL.id,
    isUseSecondaryModel: true,
    logoImage: ImageBaseIcon,
    botImage: ImageBaseIcon,
    images: {},
    imageCount: 0,
    guardrailInstructions: defaultGuardrailInstructions,
    isGuardrailsEnabled: false,
    isLocalModel: true,
    localImagePrompt: `Look at the image and list everything you see.
Include any people, objects, or actions.
Describe where people and objects are in relation to each other (for example, "a person is standing next to a cart").`,
    headerColor: "#242C42",
    promptFormatResponse: `Given the DESCRIPTION image description, and CRITERIA for a Yes No answer.

Please give your answer using markdown formatting, with a header section up top specifying Yes or No if it matches the CRITERIA.

VERY IMPORTANT: Think step-by-step about the query and share your concise thought process in three numbered thinking steps formatted <think>1) Text...</think> <think>2) Text...</think> <think>3) Text...</think>`
  },
  //     ["Audio Analysis"]: {
  //         ...CUSTOM_TEMPLATE_DETAILS,
  //         id: "Audio Analysis",
  //         name: "Audio Analysis",
  //         description: "Analyze audio files and flag when specified characteristics are detected",
  //         itemType: "Agent",
  //         wizard: {
  //             base64StringLogo: AudioTemplateIcon,
  //             imgStyle: {
  //                 padding: "5px",
  //                 background: "#18284C"
  //             }
  //         },
  //         localImagePrompt: "What is in this audio clip?",
  //         ratings: 1247,
  //         feedbackScore: 95,
  //         capabilities: ["Chat", "Agent", "Fast"],
  //         tags: [22, 25],
  //         eventFlag: "Is this clip funny? A funny clip has laughter in it.",
  //         processorType: "Grace Hopper 200",
  //         provisionLocation: "Near Edge",
  //         performanceType: "Accuracy",
  //         media: "audio",
  //         messages: [
  //             {
  //                 text: "Welcome to the Audio Analysis agent.",
  //                 date: new moment()
  //             }
  //         ],
  //         modelId: AUDIO_MODEL.id,
  //         secondaryTextModelId: AUDIO_MODEL.id,
  //         isUseSecondaryModel: true,
  //         logoImage: AudioTemplateIcon,
  //         botImage: AudioTemplateIcon,
  //         audio: {},
  //         audioCount: 0,
  //         guardrailInstructions: defaultGuardrailInstructions,
  //         isGuardrailsEnabled: false,
  //         isLocalModel: true,
  //         headerColor: "#242C42",
  //         promptFormatResponse: `I'm going to send you a text DESCRIPTION of an audio clip, as well as a CRITERIA that the clip must fit.
  // Please tell me if this clip meets the CRITERIA or not
  // Please give your answer using markdown formatting, with a header section up top specifying Yes or No if it matches the CRITERIA.`
  //     },
  //     ["Video Analysis"]: {
  //         ...CUSTOM_TEMPLATE_DETAILS,
  //         id: "Video Analysis",
  //         name: "Video Analysis",
  //         description: "Analyze streaming video and receive alerts when the behavior you are looking for happens.",
  //         itemType: "Agent",
  //         wizard: {
  //             base64StringLogo: IconPoweredCam,
  //             imgStyle: {
  //                 padding: "5px",
  //                 background: "#242C42"
  //             }
  //         },
  //         ratings: 250,
  //         feedbackScore: 96,
  //         tags: [22, 23],
  //         capabilities: ["Chat", "Agent", "Fast"],
  //         eventFlag: "What would you like to detect? Add instructions here and test by uploading images",
  //         processorType: "Grace Hopper 200",
  //         provisionLocation: "Near Edge",
  //         performanceType: "Accuracy",
  //         media: "video",
  //         messages: [
  //             {
  //                 text: "Welcome to the Video Analysis agent.",
  //                 date: new moment()
  //             }
  //         ],
  //         modelId: GEMINI_TEST.id,
  //         secondaryTextModelId: GEMINI_TEST.id,
  //         images: {},
  //         imageCount: 0,
  //         guardrailInstructions: defaultGuardrailInstructions,
  //         isGuardrailsEnabled: false,
  //         isLocalModel: false,
  //         headerColor: "#242C42",
  //         promptFormatResponse: `I would like for you to take the following image and tell me if it matches the given CRITERIA or not.
  // Please give your answer using markdown formatting, with a header section up top specifying Yes or No if it matches the CRITERIA.`
  //     },
  [TEMPLATE_DOCKYARD_SAFETY.id]: { ...TEMPLATE_DOCKYARD_SAFETY },
  [TEMPLATE_ABANDONED_LUGGAGE_DETECTION.id]: { ...TEMPLATE_ABANDONED_LUGGAGE_DETECTION },
  [TEMPLATE_PPE.id]: { ...TEMPLATE_PPE },
  [TEMPLATE_ELDER_FALL.id]: { ...TEMPLATE_ELDER_FALL },
  ["create_new"]: {
    id: "Create New",
    name: "Video Analysis",
    description: "Analyze streaming video and receive alerts when the behavior you are looking for happens.",
    eventFlag:
      "Does this image contain abandoned luggage? Abandoned luggage we will define as luggage in the image and no people in the image as well.",
    processorType: "Grace Hopper 200",
    provisionLocation: "Far Edge",
    performanceType: "Accuracy",
    messages: [
      {
        text: "Welcome to your new agent.",
        date: new moment()
      }
    ],
    modelId: IMAGE_MODEL.id,
    secondaryTextModelId: IMAGE_MODEL.id,
    isUseSecondaryModel: true,
    images: {},
    imageCount: 0,
    guardrailInstructions: defaultGuardrailInstructions,
    isGuardrailsEnabled: false
  },
  [TEMPLATE_HOG_TAIL_BITING.id]: { ...TEMPLATE_HOG_TAIL_BITING },
  [TEMPLATE_HOG_AGGRESSION.id]: { ...TEMPLATE_HOG_AGGRESSION }
};

const initialState = DEFAULT_TEMPLATES;

function templateReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_BASE:
      return {
        ...action.data
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: action.data
      };
    case DELETE_FIELD:
      const retState = { ...state };
      delete retState[action.id];
      return retState;
    case UPDATE_FIELD_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    case RESET:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

const EXAMPLE_AI_BLUEPRINT_DATABASE_OPTIONS = {
  0: {
    id: 0,
    label: "Aible Sample Store"
  }
};

export const AI_BLUEPRINT_LOCALES = {
  "en-US": {
    value: "en-US",
    label: "English (American)"
  },
  "en-GB": {
    value: "en-GB",
    label: "English (British)"
  },
  "en-AU": {
    value: "en-AU",
    label: "English (Australian)"
  },
  "en-CA": {
    value: "en-CA",
    label: "English (Canadian)"
  },
  "es-ES": {
    value: "es-ES",
    label: "Spanish (Spanish)"
  },
  "es-MX": {
    value: "es-MX",
    label: "Spanish (Mexican)"
  },
  "es-AR": {
    value: "es-AR",
    label: "Spanish (Argentinian)"
  },
  "es-CO": {
    value: "es-CO",
    label: "Spanish (Colombian)"
  },
  "fr-FR": {
    value: "fr-FR",
    label: "French (Metropolitan)"
  },
  "fr-CA": {
    value: "fr-CA",
    label: "French (Canadian)"
  }
};

const TEMPLATE_LINEAGE_CHANGES = [
  {
    value: "NAME",
    label: "Name",
    keys: ["name"],
    type: "VALUE"
  },
  {
    value: "DESCRIPTION",
    label: "Description",
    keys: ["description"],
    type: "VALUE"
  },
  {
    value: "BRANDING",
    label: "Branding",
    keys: [
      "logoImage",
      "botImage",
      "headerColor",
      "backgroundColor",
      "introText",
      {
        key: "wizard",
        subKeys: ["base64StringLogo"],
        type: "object"
      }
    ]
  },
  {
    value: "TAGS",
    label: "Tags",
    keys: [
      {
        key: "tags",
        type: "array"
      }
    ],
    type: "TAG"
  },
  {
    value: "MODEL_ID",
    label: "AI Model",
    keys: ["modelId"],
    type: "MODEL"
  },
  {
    value: "TEXT_MODEL_ID",
    label: "Event Detection and Summarization Model",
    keys: ["secondaryTextModelId"],
    type: "MODEL"
  },
  {
    value: "PROMPT",
    label: "Prompt",
    keys: ["eventFlag", "localImagePrompt", "promptFormatResponse"]
  },
  {
    value: "GUARDRAILS",
    label: "Guardrails",
    keys: ["isGuardrailsEnabled", "guardrailInstructions"]
  },
  {
    value: "DEPLOY",
    label: "Deployment Options",
    keys: ["performanceType", "processorType", "provisionLocation"]
  }
];

/* Selectors */
export function getBase(state) {
  return state.template;
}

// get the templates for the current folder
// we always show templates that don't have a numerical id or don't have an associated blueprintId
export function getTemplates(state) {
  const sFolder = folderReducer.getSelectedFolder(state);

  const templates = getBase(state);
  const retTemplates = {};
  if (isEnabled("show-media-templates")) {
    Object.keys(templates).forEach((tKey) => {
      const currTemplate = templates[tKey];
      const currBlueprintId = currTemplate.blueprintId;
      if (!currBlueprintId || isNaN(tKey)) {
        retTemplates[tKey] = currTemplate;
      } else {
        const currBlueprint = blueprintReducer.getBlueprint(state, currBlueprintId);
        if (currBlueprint?.folder_id === sFolder) {
          retTemplates[tKey] = currTemplate;
        }
      }
    });
  }

  return retTemplates;
}

export function getTemplate(state, id) {
  return getBase(state) ? getBase(state)[id] : {};
}

export function getName(state, id) {
  const template = getTemplate(state, id);

  return template?.name;
}

export function getIsSavingTemplate(state, id) {
  const template = getTemplate(state, id);

  return template?.isSavingTemplate;
}

export function getLocalImagePrompt(state, id) {
  const template = getTemplate(state, id);

  return template?.localImagePrompt ?? DEFAULT_LOCAL_IMAGE_PROMPT;
}

export function getModelId(state, id) {
  const template = getTemplate(state, id);

  return template?.modelId;
}

export function getSecondaryTextModelId(state, id) {
  const template = getTemplate(state, id);

  return template?.secondaryTextModelId;
}

export function getMessages(state, id) {
  const template = getTemplate(state, id);

  return template?.messages;
}

export function getMessagesFixMulti(state, id) {
  const messages = getMessages(state, id);

  const tMessages = messages.map((m, i) => ({ ...m, messageIndex: i }));

  let retMessages = [];

  for (const m of tMessages) {
    if (m.reasoning !== undefined) {
      retMessages.push({
        date: m.date,
        reasoning: m.reasoning,
        messageIndex: m.messageIndex
      });
    }
    if (m.multiStepMessages !== undefined) {
      for (const multiMessage of m.multiStepMessages) {
        if (multiMessage.reasoning !== undefined) {
          retMessages.push({
            date: m.date,
            reasoning: multiMessage.reasoning,
            messageIndex: m.messageIndex
          });
        }
      }
    }
  }

  return retMessages;
}

export function getAiBlueprintDatabaseOptions(state) {
  // Currently just using a label as the catalog location is hardcoded on our backend
  const retOptions = Object.keys(EXAMPLE_AI_BLUEPRINT_DATABASE_OPTIONS).map((oKey) => {
    return {
      label: EXAMPLE_AI_BLUEPRINT_DATABASE_OPTIONS[oKey]?.label,
      value: EXAMPLE_AI_BLUEPRINT_DATABASE_OPTIONS[oKey]?.label
    };
  });

  return retOptions;
}

export function getSelectedAiBlueprintDatabase(state, templateId) {
  const template = getTemplate(state, templateId);
  return template?.aiBlueprint?.databaseId;
}

export function getAiBlueprintImageFlow(state, templateId) {
  // Currently not used but may be used for individual vs batch uploading
  const template = getTemplate(state, templateId);
  return template?.aiBlueprint?.imageFlow ?? "INDIVIDUAL";
}

export function getAiBlueprintLocaleOptions(state) {
  const retOptions = Object.keys(AI_BLUEPRINT_LOCALES).map((lKey) => ({
    value: AI_BLUEPRINT_LOCALES[lKey]?.value,
    label: AI_BLUEPRINT_LOCALES[lKey]?.label
  }));

  return retOptions;
}

export function getAiBlueprintLocale(state, templateId) {
  const template = getTemplate(state, templateId);
  return template?.aiBlueprint?.locale ?? "en-US";
}

export function getAiBlueprintBrandingInstructions(state, templateId) {
  const template = getTemplate(state, templateId);
  return template?.aiBlueprint?.brandingInstructions ?? "";
}

/* Actions */
export function updateBase(templates) {
  return (dispatch, getState) => {
    return dispatch({ type: UPDATE_BASE, data: templates });
  };
}

export function resetBase() {
  return { type: RESET };
}

export function updateTemplate(templateId, template) {
  return (dispatch, getState) => {
    return dispatch({ type: UPDATE_FIELD, id: templateId, data: template });
  };
}

export function updateName(id, name) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: name,
      key: "name"
    });
  };
}

export function updateDescription(id, description) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: description,
      key: "description"
    });
  };
}

export function updateEventFlag(id, eventFlag) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: eventFlag,
      key: "eventFlag"
    });
  };
}

export function updatePromptFormatResponse(id, promptFormatResponse) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: promptFormatResponse,
      key: "promptFormatResponse"
    });
  };
}

export function updateLastImageData(id, lastImageData) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: lastImageData,
      key: "lastImageData"
    });
  };
}

export function updateLocalImagePrompt(id, localImagePrompt) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: localImagePrompt,
      key: "localImagePrompt"
    });
  };
}

export function updateProcessorType(id, processorType) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: processorType,
      key: "processorType"
    });
  };
}

export function updateProvisionLocation(id, provisionLocation) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: provisionLocation,
      key: "provisionLocation"
    });
  };
}

export function updatePerformanceType(id, performanceType) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: performanceType,
      key: "performanceType"
    });
  };
}

export function updateBackgroundColor(id, backgroundColor) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: backgroundColor,
      key: "backgroundColor"
    });
  };
}

export function updateTemplateTags(id, tags) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: tags,
      key: "tags"
    });
  };
}

export function updateGuardrailInstructions(id, guardrailInstructions) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: guardrailInstructions,
      key: "guardrailInstructions"
    });
  };
}

export function updateIsGuardrailsEnabled(id, isGuardrailsEnabled) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: isGuardrailsEnabled,
      key: "isGuardrailsEnabled"
    });
  };
}

export function updateShowAdvancedPromptOptions(id, showAdvancedPromptOptions) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: showAdvancedPromptOptions,
      key: "showAdvancedPromptOptions"
    });
  };
}

export function updateIsSavingTemplate(id, isSavingTemplate) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: isSavingTemplate,
      key: "isSavingTemplate"
    });
  };
}

export const OMNI_TEST_MODEL_EXTERNAL_ID = "nvidia/nvidia-nemotron-3-nano-omni";

export function updateModelId(id, modelId) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: modelId,
      key: "modelId"
    });
  };
}

export function updateSecondaryTextModelId(id, secondaryTextModelId) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: secondaryTextModelId,
      key: "secondaryTextModelId"
    });
  };
}

export function updateImageCount(id, imageCount) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: imageCount,
      key: "imageCount"
    });
  };
}

export function updateAudioCount(id, audioCount) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: audioCount,
      key: "audioCount"
    });
  };
}

export function updateImages(id, images) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: images,
      key: "images"
    });
  };
}

export function updateAudio(id, audio) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: audio,
      key: "audio"
    });
  };
}

export function updateHeaderColor(id, headerColor) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: headerColor,
      key: "headerColor"
    });
  };
}

export function updateLogoImage(id, logoImage) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: logoImage,
      key: "logoImage"
    });
  };
}

export function updateBotImage(id, botImage) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: botImage,
      key: "botImage"
    });
  };
}

export function updateAiBlueprintSetting(id, settingKey, data) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    const retTemplate = {
      ...template,
      ["aiBlueprint"]: {
        ...template?.aiBlueprint,
        [settingKey]: data
      }
    };

    return dispatch(updateTemplate(id, retTemplate));
  };
}

export function updateSelectedAiBluprintDatabaseId(templateId, val) {
  return (dispatch, getState) => {
    return dispatch(updateAiBlueprintSetting(templateId, "databaseId", val));
  };
}

export function createAiBlueprintDatabaseConnection(address, password) {
  // Not currently used, planned for making it real
  return async (dispatch, getState) => {
    await delay(5000);
    return Promise.resolve(EXAMPLE_AI_BLUEPRINT_DATABASE_OPTIONS?.[0]);
  };
}

export function updateAiBlueprintImageFlow(templateId, val) {
  return (dispatch, getState) => {
    return dispatch(updateAiBlueprintSetting(templateId, "imageFlow", val));
  };
}

export function updateAiBlueprintLocale(templateId, val) {
  return (dispatch, getState) => {
    return dispatch(updateAiBlueprintSetting(templateId, "locale", val));
  };
}

export function updateAiBlueprintBrandingInstructions(templateId, val) {
  return (dispatch, getState) => {
    return dispatch(updateAiBlueprintSetting(templateId, "brandingInstructions", val));
  };
}

export function addImage(id, image, isFeedbackGood = true, mIndex = -1) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    const images = template?.images ?? {};

    dispatch(
      updateImages(id, {
        ...images,
        [template?.imageCount]: {
          image: image,
          isFeedbackGood: isFeedbackGood === true ? true : false,
          mIndex: mIndex === -1 ? undefined : mIndex
        }
      })
    );
    dispatch(updateImageCount(id, (template?.imageCount ?? 0) + 1));
  };
}

export function updateImageFeedback(id, imageKey, isFeedbackGood) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    const images = template?.images ?? {};

    dispatch(
      updateImages(id, {
        ...images,
        [imageKey]: { ...images?.[imageKey], isFeedbackGood: isFeedbackGood }
      })
    );
  };
}

export function deleteImage(id, imageKey) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    let images = template?.images ?? {};
    delete images?.[imageKey];

    dispatch(updateImages(id, { ...images }));
  };
}

// I wish we had generalized the media feedback so I dont have to make audio specific stuff
export function addAudio(id, audio, isFeedbackGood = true, mIndex = -1) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    const templateAudio = template?.audio ?? {};
    const currKey = template?.audioCount ?? 0;

    dispatch(
      updateAudio(id, {
        ...templateAudio,
        [currKey]: {
          audio: audio,
          isFeedbackGood: isFeedbackGood === true ? true : false,
          mIndex: mIndex === -1 ? undefined : mIndex
        }
      })
    );
    dispatch(updateAudioCount(id, currKey + 1));
  };
}

export function updateAudioFeedback(id, audioKey, isFeedbackGood) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    const audio = template?.audio ?? {};

    dispatch(
      updateAudio(id, {
        ...audio,
        [audioKey]: { ...audio?.[audioKey], isFeedbackGood: isFeedbackGood }
      })
    );
  };
}

export function deleteAudio(id, audioKey) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    let audio = template?.audio ?? {};
    delete audio?.[audioKey];

    dispatch(updateAudio(id, { ...audio }));
  };
}

export function updateMessages(id, messages) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: messages,
      key: "messages"
    });
  };
}

export function updateMessage(id, index, message) {
  return (dispatch, getState) => {
    const messages = getTemplate(getState(), id)?.messages ?? [];
    const retMessages = [...messages];
    retMessages.splice(index, 1, message);

    return dispatch(updateMessages(id, retMessages));
  };
}

export function addMultiStepMessage(id, index, message) {
  return (dispatch, getState) => {
    const messages = getTemplate(getState(), id)?.messages ?? [];
    const retMessage = messages[index];
    const multiStepMessages = retMessage.multiStepMessages ?? [];
    const retMultiStepMessages = [...multiStepMessages, message];
    retMessage.multiStepMessages = retMultiStepMessages;
    return dispatch(updateMessage(id, index, retMessage));
  };
}

export function addMessage(id, message) {
  return (dispatch, getState) => {
    const messages = getTemplate(getState(), id)?.messages ?? [];
    const retMessages = [...messages, message];
    dispatch(updateMessages(id, retMessages));

    const scroller = document.getElementById("template_message_scroll");
    if (scroller) {
      setTimeout(() => {
        scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
      }, 200);
    }
    return retMessages?.length - 1;
  };
}

export function updateGeneratedEventFlag(id, generatedEventFlag) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: generatedEventFlag,
      key: "generatedEventFlag"
    });
  };
}

export function updateIsGeneratingEventFlag(id, isGeneratingEventFlag) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: isGeneratingEventFlag,
      key: "isGeneratingEventFlag"
    });
  };
}

export function updateIsShowImageModal(id, isShowImageModal) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: isShowImageModal,
      key: "isShowImageModal"
    });
  };
}

export function updateIsShowAudioModal(id, isShowAudioModal) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: isShowAudioModal,
      key: "isShowAudioModal"
    });
  };
}

export function updateIsApproveImprovedPrompt(id, isApproveImprovedPrompt) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: isApproveImprovedPrompt,
      key: "isApproveImprovedPrompt"
    });
  };
}

export function generateEventFlag(id, imageData, updatedCriteria) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), id);

    dispatch(updateIsGeneratingEventFlag(id, true));

    if (template?.images && Object.keys(template.images).length > 0) {
      for (var i = 0; i < Object.keys(template.images).length; i++) {
        const image = template.images[Object.keys(template.images)?.[i]];

        const updatedTemplate = getTemplate(getState(), id);
        await dispatch(
          generateEventFlagImage(id, image, updatedTemplate?.generatedEventFlag ?? updatedTemplate?.eventFlag, i)
        );
      }

      dispatch(
        addMessage(id, {
          user: "LLM",
          text: "Event detection prompt updated based on " + Object.keys(template.images).length + " image examples"
        })
      );
    }

    const templateAudioKeys = Object.keys(template?.audio ?? {});
    if (template?.audio && templateAudioKeys.length > 0) {
      for (var i = 0; i < templateAudioKeys.length; i++) {
        const templateAudio = template.audio[templateAudioKeys?.[i]];

        const updatedTemplate = getTemplate(getState(), id);
        await dispatch(
          generateEventFlagAudio(
            id,
            templateAudio,
            updatedTemplate?.generatedEventFlag ?? updatedTemplate?.eventFlag,
            i
          )
        );
      }

      dispatch(
        addMessage(id, {
          user: "LLM",
          text: "Event detection prompt updated based on " + Object.keys(template.images).length + " image examples"
        })
      );
    }

    dispatch(updateIsApproveImprovedPrompt(id, false));

    return dispatch(updateIsGeneratingEventFlag(id, false));
  };
}

export function generateEventFlagAudio(id, audio, updatedCriteria, index) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), id);

    return Promise.resolve();
  };
}

export function generateEventFlagImage(id, image, updatedCriteria, index) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), id);

    return dispatch(
      template?.isLocalModel
        ? generateEventFlagImage_local(id, image, updatedCriteria, index)
        : generateEventFlagImage_gemini(id, image, updatedCriteria, index)
    );
  };
}

export function generateEventFlagImage_local(id, image, updatedCriteria, index) {
  return async (dispatch, getState) => {
    const modelId = getModelId(getState(), id);
    const model = aiServiceReducer.getModelById(getState(), modelId);

    const genConfig = {
      type: "generate_content",
      generation_config: {
        model_id: model?.id,
        model: model?.external_id,
        max_tokens: 1500
      },
      use_case_name: "LLM",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: [
        {
          role: "user",
          content: [
            {
              type: "data",
              mime_type: "image/jpeg",
              data: (image?.image ?? "").replace("data:image/jpeg;base64,", "")
            }
          ]
        }
      ]
    };

    console.log("what is genPrompt config", genConfig);

    const generatePrompt = await dispatch(chatGPTReducer.doCompletion(genConfig));

    const GENERATE_EVENT_FLAG_PROMPT = `I'm going to give you a CRITERIA for images.
        I'm also going to give you a description of an image as example that need to also fit in the UPDATED CRITERIA.
        I'd like you to edit the CRITERIA and add to it using the example images, but be sure that the UPDATED CRITERIA works with BOTH the original CRITERIA and the example images!
        Don't be afraid to combine different things with OR, like if the original CRITERIA was for Banana's and an example image has a dog, the UPDATED CRITERIA can be either Bananas or Dogs, etc.
        Please return only the UPDATED CRITERIA in your response and nothing more!
        If the Image Description is nonsense then just return the original CRITERIA.
        Thanks!`;

    let messageArray = [
      {
        role: "system",
        content: GENERATE_EVENT_FLAG_PROMPT
      }
    ];

    const uContent =
      "CRITERIA: " +
      updatedCriteria +
      "\nThis is an example of an image which should " +
      (image?.isFeedbackGood ? "" : "NOT") +
      " fit the CRITERIA\n" +
      "Image Description: " +
      generatePrompt?.data?.output?.content;

    messageArray.push({
      role: "user",
      content: uContent
    });

    const secondaryTextModelId = getSecondaryTextModelId(getState(), id) ?? modelId;
    let secondaryTextModel = aiServiceReducer.getModelById(getState(), secondaryTextModelId);
    if (!secondaryTextModel) {
      secondaryTextModel = aiServiceReducer.getModelById(getState(), modelId);
    }

    const genConfigText = {
      type: "generate_content",
      generation_config: {
        model_id: secondaryTextModel?.id,
        model: secondaryTextModel?.external_id,
        max_tokens: 100
      },
      use_case_name: "LLM",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: messageArray
    };

    dispatch(
      addMessage(id, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        title: "Image " + (index + 1) + " context evaluation in progress",
        text: GENERATE_EVENT_FLAG_PROMPT + "\n\n" + uContent,
        isModal: true
      })
    );

    const generateTextPrompt = await dispatch(chatGPTReducer.doCompletion(genConfigText));

    const uCriteria = (generateTextPrompt?.data?.output?.content ?? "").replace("UPDATED CRITERIA:", "");

    dispatch(updateGeneratedEventFlag(id, uCriteria));

    const template = getTemplate(getState(), id);

    let retMessages = [...(template?.messages ?? [])];
    if (retMessages?.[Object.keys(retMessages).length - 1] !== undefined) {
      retMessages[Object.keys(retMessages).length - 1].title = "Image " + (index + 1) + " context evaluation complete";
      retMessages[Object.keys(retMessages).length - 1].response = generatePrompt?.data?.output?.content;
    }

    dispatch(updateMessages(id, retMessages));

    console.log("what is generatePrompt", generatePrompt, generateTextPrompt);

    return generateTextPrompt;
  };
}

export function generateEventFlagImage_gemini(id, image, updatedCriteria, index) {
  return async (dispatch, getState) => {
    const GENERATE_EVENT_FLAG_PROMPT = `I'm going to give you a CRITERIA for images.
I'm also going to give you a few images as examples that need to also fit in the UPDATED CRITERIA.
I'd like you to edit the CRITERIA and add to it using the example images, but be sure that the UPDATED CRITERIA works with BOTH the ORIGINAL CRITERIA and the example images!
Don't be afraid to combine different things with OR, like if the ORIGINAL CRITERIA was for Banana's and an example image has a dog, the UPDATED CRITERIA can be either Bananas or Dogs, etc.
Thanks!`;

    let messageArray = [
      {
        role: "system",
        content: GENERATE_EVENT_FLAG_PROMPT
      }
    ];

    messageArray.push({
      role: "user",
      content: "ORIGINAL CRITERIA: " + updatedCriteria
    });

    messageArray.push({
      role: "user",
      content: [
        {
          type: "text",
          text:
            "This is an example of an image which should " + (image?.isFeedbackGood ? "" : "NOT") + " fit the CRITERIA"
        },
        {
          type: "data",
          mime_type: "image/jpeg",
          data: (image?.image ?? "").replace("data:image/jpeg;base64,", "")
        }
      ]
    });

    messageArray.push({
      role: "assistant",
      content: "UPDATED CRITERIA: "
    });

    const modelId = getModelId(getState(), id);
    const model = aiServiceReducer.getModelById(getState(), modelId);

    const genConfig =
      id === "Video Analysis Local"
        ? {
            type: "generate_content",
            generation_config: {
              model_id: model?.id,
              model: model?.external_id,
              max_tokens: 1500
            },
            use_case_name: "LLM",
            folder_id: folderReducer.getSelectedFolder(getState()),
            messages: messageArray
          }
        : {
            type: "generate_content",
            generation_config: {
              model_id: model?.id,
              model: model?.external_id,
              max_tokens: 1500
            },
            use_case_name: "LLM",
            folder_id: folderReducer.getSelectedFolder(getState()),
            messages: messageArray
          };

    console.log("what is genPrompt config", genConfig);

    dispatch(
      addMessage(id, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        title: "Image " + (index + 1) + " context evaluation in progress",
        text:
          GENERATE_EVENT_FLAG_PROMPT +
          "\n\n" +
          "ORIGINAL CRITERIA: " +
          updatedCriteria +
          "\n\n" +
          "This is an example of an image which should " +
          (image?.isFeedbackGood ? "" : "NOT") +
          " fit the CRITERIA" +
          "\n\n"["[IMAGE DATA OMITTED]"],
        isModal: true
      })
    );

    const generatePrompt = await dispatch(chatGPTReducer.doCompletion(genConfig));

    dispatch(updateGeneratedEventFlag(id, generatePrompt?.data?.output?.content));

    const template = getTemplate(getState(), id);

    let retMessages = [...(template?.messages ?? [])];
    if (retMessages?.[Object.keys(retMessages).length - 1] !== undefined) {
      retMessages[Object.keys(retMessages).length - 1].title = "Image " + (index + 1) + " context evaluation complete";
      retMessages[Object.keys(retMessages).length - 1].response = generatePrompt?.data?.output?.content;
    }

    dispatch(updateMessages(id, retMessages));

    return generatePrompt;
  };
}

export function audioTest(id, audioData) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    const isLocal = template?.isLocalModel;

    const retEventFlag =
      template?.generatedEventFlag !== undefined && template?.isApproveImprovedPrompt === true
        ? template?.generatedEventFlag
        : template?.eventFlag;

    const messageArray = [];
    let prompt = getLocalImagePrompt(getState(), id);
    let userText = prompt;

    if (!isLocal) {
      prompt = template?.promptFormatResponse;
      userText = "CRITERIA:\n" + retEventFlag;
      messageArray.push({
        role: "system",
        content: prompt
      });
    }

    messageArray.push({
      role: "user",
      content: [
        {
          type: "text",
          text: userText
        },
        {
          type: "data",
          mime_type: "audio/mp3",
          data: audioData.replace("data:audio/mpeg;base64,", "").replace("data:audio/mp3;base64,", "")
        }
      ]
    });

    const modelId = getModelId(getState(), id);
    const model = aiServiceReducer.getModelById(getState(), modelId);

    let genConfig = {
      type: "generate_content",
      generation_config: {
        ...chatGPTReducer.getGenerationConfig(getState()),
        model_id: model?.id,
        model: model?.external_id,
        max_tokens: 100
      },
      use_case_name: "LLM",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: messageArray
    };

    // Use gemini for now
    if (!isLocal) {
      genConfig.generation_config = {
        model: "gemini-2.0-flash-exp",
        max_tokens: 1500
      };
    } else {
      // We need to add this mode if we're using the audio model
      genConfig.mode = "audiolm";
    }

    console.log("is local? ", isLocal);
    console.log("prompt", prompt);
    console.log("what is genConfig", genConfig);

    dispatch(
      addMessage(id, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        text: prompt,
        audio: audioData,
        date: new moment()
      })
    );

    let questionIndex = -1;
    if (template?.isGuardrailsEnabled) {
      questionIndex = dispatch(
        addMessage(id, {
          user: "LLM",
          date: new moment(),
          isMultiStep: "guardrails",
          useGuardrails: true
        })
      );
    }
    dispatch(tagsReducer.updateActiveCapability(0));
    let audioTestResult;

    try {
      audioTestResult = await dispatch(chatGPTReducer.doCompletion(genConfig));
    } catch (e) {
      console.log("Audio test failed: ", e);
      dispatch(
        handleAgentError(e, template?.id, undefined, undefined, questionIndex !== -1 ? questionIndex : undefined)
      );
    }

    if (isLocal) {
      const firstAudioTestResult = audioTestResult;
      dispatch(
        addMessage(id, {
          user: "LLM",
          text: firstAudioTestResult?.data?.output?.content,
          date: new moment()
        })
      );

      const textPrompt = template?.promptFormatResponse;

      const secondaryTextModelId = getSecondaryTextModelId(getState(), id) ?? modelId;
      let secondaryTextModel = aiServiceReducer.getModelById(getState(), secondaryTextModelId);
      if (!secondaryTextModel) {
        secondaryTextModel = aiServiceReducer.getModelById(getState(), modelId);
      }

      const genConfig_llamaText = {
        type: "generate_content",
        generation_config: {
          model_id: secondaryTextModel?.id,
          model: secondaryTextModel?.external_id,
          max_tokens: 500
          // "model": "gemini-2.0-flash-exp",
          // "max_tokens": 1500
        },
        use_case_name: "LLM",
        folder_id: folderReducer.getSelectedFolder(getState()),
        messages: [
          {
            role: "system",
            content: textPrompt
          },
          {
            role: "user",
            content: "CRITERIA:\n" + retEventFlag + "\nDESCRIPTION:\n" + firstAudioTestResult?.data?.output?.content
          }
        ]
      };

      dispatch(
        addMessage(id, {
          user: "User",
          userInitials: loginReducer.getUserInitials(getState()),
          text:
            textPrompt +
            "\n\n" +
            "CRITERIA:\n" +
            retEventFlag +
            "\nDESCRIPTION:\n" +
            firstAudioTestResult?.data?.output?.content,
          date: new moment()
        })
      );
      dispatch(tagsReducer.updateActiveCapability(1));

      try {
        audioTestResult = await dispatch(chatGPTReducer.doCompletion(genConfig_llamaText));
      } catch (e) {
        console.log("Final audio test failed: ", e);
        dispatch(
          handleAgentError(e, template?.id, undefined, undefined, questionIndex !== -1 ? questionIndex : undefined)
        );
      }

      console.log("audioTestFinalLocalResult", audioTestResult);
    }
    dispatch(tagsReducer.updateActiveCapability(1));
    if (template?.isGuardrailsEnabled && questionIndex !== -1) {
      try {
        const fixJSON = (audioTestResult?.data?.output?.content ?? "").replace("```json", "").replace("```", "");
        const parseResult = JSON.parse(fixJSON);

        dispatch(
          addMultiStepMessage(id, questionIndex, {
            user: "LLM",
            text: parseResult?.reason,
            result: parseResult?.result,
            description: parseResult?.description,
            date: new moment()
          })
        );

        await dispatch(handleGuardrails(id, audioTestResult?.data?.output?.content, false, questionIndex));

        dispatch(
          addMessage(id, {
            user: "LLM",
            fewShotAudio: audioData,
            date: new moment()
          })
        );
      } catch (e) {
        console.log("Error parsing result", e);

        dispatch(
          addMultiStepMessage(id, questionIndex, {
            user: "LLM",
            text: audioTestResult?.data?.output?.content,
            date: new moment()
          })
        );

        await dispatch(handleGuardrails(id, audioTestResult?.data?.output?.content, false, questionIndex));

        dispatch(
          addMessage(id, {
            user: "LLM",
            fewShotAudio: audioData,
            date: new moment()
          })
        );
      }

      console.log("what is audioTestResult", audioTestResult, audioTestResult?.data?.output?.content);
      const activeCapability = tagsReducer.getActiveCapability(getState());
      dispatch(tagsReducer.updateActiveCapability(activeCapability + 1));
      dispatch(viewReducer.updateTemplateSetupTab("Try"));
      return Promise.resolve();
    }

    try {
      const fixJSON = (audioTestResult?.data?.output?.content ?? "").replace("```json", "").replace("```", "");
      const parseResult = JSON.parse(fixJSON);

      dispatch(
        addMessage(id, {
          user: "LLM",
          text: parseResult?.reason,
          result: parseResult?.result,
          description: parseResult?.description,
          date: new moment()
        })
      );

      dispatch(
        addMessage(id, {
          user: "LLM",
          fewShotAudio: audioData,
          date: new moment()
        })
      );
    } catch (e) {
      console.log("Error parsing result", e);

      dispatch(
        addMessage(id, {
          user: "LLM",
          text: audioTestResult?.data?.output?.content,
          date: new moment()
        })
      );

      dispatch(
        addMessage(id, {
          user: "LLM",
          fewShotAudio: audioData,
          date: new moment()
        })
      );
    }

    console.log("what is audioTestResult", audioTestResult, audioTestResult?.data?.output?.content);
    const activeCapability = tagsReducer.getActiveCapability(getState());
    dispatch(tagsReducer.updateActiveCapability(activeCapability + 1));
    dispatch(viewReducer.updateTemplateSetupTab("Try"));
    return Promise.resolve();
  };
}

export function imageTest(id, imageData, promptExtra = undefined) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), id);

    const retImageData = imageData === undefined ? template?.lastImageData : imageData;

    if (imageData !== undefined) {
      dispatch(updateLastImageData(id, imageData));
    }

    const modelId = getModelId(getState(), id);

    console.log("What is modelId", modelId);

    return dispatch(
      template?.isLocalModel
        ? imageTest_Local_Single(id, retImageData, promptExtra)
        : imageTest_Gemini(id, retImageData, promptExtra)
    );
  };
}

const parseImageResponse = (content) => {
  if (!content) return { result: null };
  // Match single-hash header (# Label), not double-hash (## Observation)
  const headerMatch = content.match(/^#\s+(.+?)\s*$/m);
  return { result: headerMatch ? headerMatch[1].trim() : null };
};

// Convert "1) ... 2) ... 3) ..." into "<think>1) ...</think>\n<think>2) ...</think>\n<think>3) ...</think>"
// so the UI's ReasoningMessage component renders them as "Thinking: Step 1/2/3" cards.
// We do this in JS because nemotron-nano's tokenizer mangles the opening <think> tag
// into "Tra" garbage when it tries to emit it.
const wrapReasoningStepsInThinkTags = (text) => {
  if (!text) return text;

  // Find where the first numbered step begins
  const firstStepIndex = text.search(/^\s*\d+\)/m);
  if (firstStepIndex === -1) return text;

  const beforeSteps = text.slice(0, firstStepIndex).trimEnd();
  const stepsBlock = text.slice(firstStepIndex);

  // Split the block on lines starting with a number + ")"
  // Each match captures the full step including any wrapped text until the next numbered step
  const stepRegex = /^\s*(\d+\))\s*([\s\S]+?)(?=^\s*\d+\)|$(?![\r\n]))/gm;
  const steps = [];
  let match;
  while ((match = stepRegex.exec(stepsBlock)) !== null) {
    const num = match[1];
    const content = match[2].trim();
    steps.push(`<think>${num} ${content}</think>`);
  }

  if (steps.length === 0) return text;

  return `${beforeSteps}\n${steps.join("\n")}`;
};

export function imageTest_Local_Single(id, imageData, promptExtra = undefined) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), id);

    const isReasoning = template?.id === "Image Analysis (Reasoning)" || template?.media === "image_reasoning";
    const localImagePrompt = getLocalImagePrompt(getState(), id);

    const modelId = getModelId(getState(), id);
    const model = aiServiceReducer.getModelById(getState(), modelId);
    console.log("what is modelid, model", modelId, model);

    const retEventFlag =
      template?.generatedEventFlag !== undefined && template?.isApproveImprovedPrompt === true
        ? template?.generatedEventFlag
        : template?.eventFlag;

    const textPrompt = template?.promptFormatResponse + (promptExtra !== undefined ? promptExtra : "");

    const systemPrompt = `${textPrompt}

    CRITERIA:
    ${retEventFlag}

    === FORMAT OVERRIDE (this supersedes any earlier format instructions) ===
    Your response must contain exactly two sections in this order:

    SECTION 1 — Observation (starts with "## Observation"):
    A detailed description of what you see in the image. Start with a one-line summary of the objects and people present. Then use a bulleted list for each relevant object or entity category. Then one or two short paragraphs about proximity, context, and any other relevant details. This section is a neutral observation, NOT a judgment.

    SECTION 2 — Result (starts with a single-hash header "# [Answer label]"):
    The answer label from the criteria, followed by "Justification:" on the next line, followed by exactly three numbered reasoning steps.

    Here is the exact structure to follow:

    ## Observation
    Yes, I see [brief summary of what is in the image].

    - **[Category A]**: [description of what you observe in this category].
    - **[Category B]**: [description of what you observe in this category].
    - **[Category C]**: [description of what you observe in this category].

    [One or two short paragraphs describing spatial relationships, context, and any other relevant details.]

    # [Answer label]
    Justification:
    1) [First reasoning step explaining how the observation relates to the criteria.]
    2) [Second reasoning step building on the first.]
    3) [Third reasoning step reaching the conclusion.]

    Rules:
    - The "# [Answer label]" header must use exactly one # and must match one of the answer labels defined in the criteria.
    - Use plain numbered lines for the three reasoning steps: "1)", "2)", "3)". Do not use any XML tags, angle brackets, or special prefixes.
    - Do not add any text before "## Observation" or after the third reasoning step.`;

    const messageArray = [
      {
        role: "system",
        content: systemPrompt
      },
      {
        role: "user",
        content: [
          {
            type: "text",
            text: "Evaluate this image against the criteria."
          },
          {
            type: "data",
            mime_type: "image/jpeg",
            data: imageData.replace("data:image/jpeg;base64,", "")
          }
        ]
      }
    ];

    const genConfig = {
      type: "generate_content",
      generation_config: {
        ...chatGPTReducer.getGenerationConfig(getState()),
        presence_penalty: 0,
        model_id: model?.id,
        model: model?.external_id,
        max_tokens: isReasoning ? 2000 : 1000
      },
      use_case_name: "LLM",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: messageArray
    };

    console.log("what is genConfig_local", genConfig);

    dispatch(
      addMessage(id, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        text: localImagePrompt,
        image: imageData,
        date: new moment()
      })
    );

    const questionIndex = dispatch(
      addMessage(id, {
        user: "LLM",
        date: new moment(),
        isMultiStep: "image_local",
        useGuardrails: template?.isGuardrailsEnabled
      })
    );
    dispatch(tagsReducer.updateActiveCapability(0));

    let imageTestResult;
    try {
      imageTestResult = await dispatch(chatGPTReducer.doCompletion(genConfig));
    } catch (e) {
      console.log("Image test failed: ", e);
      dispatch(handleAgentError(e, template?.id, undefined, undefined, questionIndex));
    }

    console.log("imageTestResult", imageTestResult);

    const isCached = imageTestResult?.is_cached === true;
    const responseContent = imageTestResult?.data?.output?.content ?? "";

    dispatch(tagsReducer.updateActiveCapability(1));

    const { result: parsedResult } = parseImageResponse(responseContent);

    // Split response into Observation section and Result section
    const resultHeaderRegex = /^#\s+.+$/m;
    const resultHeaderMatch = responseContent.match(resultHeaderRegex);
    const resultHeaderIndex = resultHeaderMatch ? responseContent.indexOf(resultHeaderMatch[0]) : -1;

    let observationSection = "";
    let resultSection = responseContent;

    if (resultHeaderIndex >= 0) {
      const beforeResult = responseContent.slice(0, resultHeaderIndex).trim();
      // Strip the "## Observation" header since the UI step label already says that
      observationSection = beforeResult.replace(/^##\s+Observation\s*\n?/i, "").trim();
      resultSection = responseContent.slice(resultHeaderIndex).trim();
    }

    if (!observationSection) {
      observationSection = "Evaluated the image against the criteria.";
    }

    // === Step 0: "Evaluating what is in the image" — observation section ===
    dispatch(
      addMultiStepMessage(id, questionIndex, {
        user: "LLM",
        text: observationSection,
        date: new moment()
      })
    );

    // === Step 1: "Evaluating if the image contains the event you want to flag" ===
    dispatch(
      addMultiStepMessage(id, questionIndex, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        text: "CRITERIA:\n" + retEventFlag,
        date: new moment()
      })
    );

    // === Step 2: "Generating results" — full result section (header + justification) ===
    try {
      if (!parsedResult) {
        throw new Error("Could not extract result header from model response");
      }

      const retMessage = {
        user: "LLM",
        text: resultSection,
        result: parsedResult,
        description: observationSection,
        date: new moment()
      };

      console.log("what is retMessage", retMessage);

      dispatch(addMultiStepMessage(id, questionIndex, retMessage));

      if (template?.isGuardrailsEnabled) {
        await dispatch(handleGuardrails(id, responseContent, true, questionIndex, isReasoning, isCached));
      }

      dispatch(
        addMessage(id, {
          user: "LLM",
          fewShotImage: imageData,
          date: new moment()
        })
      );
    } catch (e) {
      console.log("Error parsing result", e);

      const retMessage = {
        user: "LLM",
        text: responseContent
      };

      dispatch(addMultiStepMessage(id, questionIndex, retMessage));

      if (template?.isGuardrailsEnabled) {
        await dispatch(handleGuardrails(id, responseContent, true, questionIndex, isReasoning, isCached));
      }

      dispatch(
        addMessage(id, {
          user: "LLM",
          fewShotImage: imageData,
          date: new moment()
        })
      );
    }

    // Outer message text — result section so the top-line shows
    // "# Not Abandoned" + the three numbered justification steps.
    if (!template?.isGuardrailsEnabled) {
      const template = getTemplate(getState(), id);
      const retMessage = {
        ...template?.messages[questionIndex],
        text: isReasoning ? "" : resultSection
      };
      if (isCached) {
        retMessage.isCachedResponse = true;
      }
      dispatch(updateMessage(id, questionIndex, retMessage));
    }

    console.log("what is imageTestResult", imageTestResult, imageTestResult?.data?.output?.content);

    const activeCapability = tagsReducer.getActiveCapability(getState());
    dispatch(tagsReducer.updateActiveCapability(activeCapability + 1));
    dispatch(viewReducer.updateTemplateSetupTab("Try"));
    return Promise.resolve();
  };
}

export function imageTest_Local(id, imageData, promptExtra = undefined) {
  return async (dispatch, getState) => {
    console.log("are we running imageTest_local", id, imageData, promptExtra);
    const template = getTemplate(getState(), id);

    const isReasoning = template?.id === "Image Analysis (Reasoning)" || template?.media === "image_reasoning";

    const localImagePrompt = getLocalImagePrompt(getState(), id);

    let messageArray = [
      {
        role: "user",
        content: [
          {
            type: "text",
            text: localImagePrompt
          },
          {
            type: "data",
            mime_type: "image/jpeg",
            data: imageData.replace("data:image/jpeg;base64,", "")
          }
        ]
      }
    ];

    const modelId = getModelId(getState(), id);
    const model = aiServiceReducer.getModelById(getState(), modelId);

    console.log("what is modelid, model", modelId, model);

    const genConfig_llama = {
      type: "generate_content",
      generation_config: {
        ...chatGPTReducer.getGenerationConfig(getState()),
        presence_penalty: 0,
        model_id: model?.id,
        model: model?.external_id,
        max_tokens: 500
      },
      use_case_name: "LLM",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: messageArray
    };

    const genConfig = genConfig_llama;

    console.log("what is genConfig_local", genConfig);

    dispatch(
      addMessage(id, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        text: localImagePrompt,
        image: imageData,
        date: new moment()
      })
    );

    const questionIndex = dispatch(
      addMessage(id, {
        user: "LLM",
        date: new moment(),
        isMultiStep: "image_local",
        useGuardrails: template?.isGuardrailsEnabled
      })
    );
    dispatch(tagsReducer.updateActiveCapability(0));
    let imageTestResult;
    try {
      imageTestResult = await dispatch(chatGPTReducer.doCompletion(genConfig));
    } catch (e) {
      console.log("Image test failed: ", e);
      dispatch(handleAgentError(e, template?.id, undefined, undefined, questionIndex));
    }

    dispatch(
      addMultiStepMessage(id, questionIndex, {
        user: "LLM",
        text: imageTestResult?.data?.output?.content,
        date: new moment()
      })
    );

    dispatch(tagsReducer.updateActiveCapability(1));

    const textPrompt = template?.promptFormatResponse + (promptExtra !== undefined ? promptExtra : "");

    const retEventFlag =
      template?.generatedEventFlag !== undefined && template?.isApproveImprovedPrompt === true
        ? template?.generatedEventFlag
        : template?.eventFlag;

    const secondaryTextModelId = getSecondaryTextModelId(getState(), id) ?? modelId;
    let secondaryTextModel = aiServiceReducer.getModelById(getState(), secondaryTextModelId);
    if (!secondaryTextModel) {
      secondaryTextModel = aiServiceReducer.getModelById(getState(), modelId);
    }

    const genConfig_llamaText = {
      type: "generate_content",
      generation_config: {
        model_id: secondaryTextModel?.id,
        model: secondaryTextModel?.external_id,
        max_tokens: isReasoning ? 2000 : 500
      },
      use_case_name: "LLM",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: [
        {
          role: "system",
          content: textPrompt
        },
        {
          role: "user",
          content: "CRITERIA:\n" + retEventFlag + "DESCRIPTION:\n" + imageTestResult?.data?.output?.content
        }
      ]
    };

    dispatch(
      addMultiStepMessage(id, questionIndex, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        text:
          textPrompt +
          "\n\n" +
          "CRITERIA:\n" +
          retEventFlag +
          "DESCRIPTION:\n" +
          imageTestResult?.data?.output?.content,
        date: new moment()
      })
    );
    let imageTestFinalResult;
    try {
      imageTestFinalResult = await dispatch(chatGPTReducer.doCompletion(genConfig_llamaText));
    } catch (e) {
      console.log("Final image test failed: ", e);
      dispatch(handleAgentError(e, template?.id, undefined, undefined, questionIndex));
    }

    console.log("imageTestFinalResult", imageTestFinalResult);

    const isCached = imageTestFinalResult?.is_cached === true;

    let tagIndex = (imageTestFinalResult?.data?.output?.content ?? "").indexOf("<think>");
    // let retReasoningText = tagIndex !== -1 ?
    //     (imageTestFinalResult?.data?.output?.content ?? "").substring(0, tagIndex) :
    //     imageTestFinalResult?.data?.output?.content;

    let taglessReasoningText =
      tagIndex !== -1
        ? imageTestFinalResult?.data?.output?.content?.replace(/<\/?think>/g, "")
        : imageTestFinalResult?.data?.output?.content;

    try {
      const fixJSON = (imageTestFinalResult?.data?.output?.content ?? "").replace("```json", "").replace("```", "");
      const parseResult = JSON.parse(fixJSON);

      const retMessage = isReasoning
        ? {
            user: "LLM",
            text: imageTestFinalResult?.data?.output?.content ?? "",
            result: parseResult?.result,
            description: imageTestResult?.data?.output?.content,
            date: new moment(),
            reasoning: {
              executionId: imageTestFinalResult?.id
            }
          }
        : {
            user: "LLM",
            text: parseResult?.reason,
            result: parseResult?.result,
            description: imageTestResult?.data?.output?.content,
            date: new moment()
          };

      dispatch(addMultiStepMessage(id, questionIndex, retMessage));

      if (template?.isGuardrailsEnabled) {
        await dispatch(
          handleGuardrails(id, imageTestFinalResult?.data?.output?.content, true, questionIndex, isReasoning, isCached)
        );
      }

      dispatch(
        addMessage(id, {
          user: "LLM",
          fewShotImage: imageData,
          date: new moment()
        })
      );
    } catch (e) {
      console.log("Error parsing result", e);

      const retMessage = isReasoning
        ? {
            user: "LLM",
            text: taglessReasoningText,
            reasoning: {
              executionId: imageTestFinalResult?.id
            }
          }
        : {
            user: "LLM",
            text: imageTestFinalResult?.data?.output?.content
          };

      dispatch(addMultiStepMessage(id, questionIndex, retMessage));

      if (template?.isGuardrailsEnabled) {
        await dispatch(
          handleGuardrails(id, imageTestFinalResult?.data?.output?.content, true, questionIndex, isReasoning, isCached)
        );
      }

      dispatch(
        addMessage(id, {
          user: "LLM",
          fewShotImage: imageData,
          date: new moment()
        })
      );
    }

    if (!template?.isGuardrailsEnabled) {
      const template = getTemplate(getState(), id);
      const retMessage = {
        ...template?.messages[questionIndex],
        text: isReasoning ? "" : imageTestFinalResult?.data?.output?.content
      };
      if (isCached) {
        retMessage.isCachedResponse = true;
      }

      dispatch(updateMessage(id, questionIndex, retMessage));
    }

    console.log(
      "what is imageTestResult, imageTestFinalResult",
      imageTestResult,
      imageTestResult?.data?.output?.content,
      imageTestFinalResult,
      imageTestFinalResult?.data?.output?.content
    );
    const activeCapability = tagsReducer.getActiveCapability(getState());
    dispatch(tagsReducer.updateActiveCapability(activeCapability + 1));
    dispatch(viewReducer.updateTemplateSetupTab("Try"));
    return Promise.resolve();
  };
}

export function imageTest_Gemini(id, imageData) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), id);

    const retEventFlag =
      template?.generatedEventFlag !== undefined && template?.isApproveImprovedPrompt === true
        ? template?.generatedEventFlag
        : template?.eventFlag;

    const IMAGE_PROMPT = template?.promptFormatResponse;

    let messageArray = [
      {
        role: "system",
        content: IMAGE_PROMPT
      }
    ];

    /*
        if (Object.keys(template?.images ?? {}).length > 0) {
            Object.keys(template?.images).forEach(iKey => {
                let contentArray = [
                    {
                        "type": "text",
                        "text": "The following is the image we are going to judge if it meets the CRITERIA!"
                        //"text": "The following image is " + (template?.images?.[iKey]?.isFeedbackGood ? "an example" : "NOT an example") + " of the CRITERIA."
                    },
                    {
                        "type": "data",
                        "mime_type": "image/jpeg",
                        "data": (template?.images?.[iKey]?.image ?? "").replace("data:image/jpeg;base64,", "")
                    },
                ]

                messageArray.push({
                    "role": "user",
                    "content": contentArray
                });
                messageArray.push({
                    "role": "assistant",
                    "content": JSON.stringify(RESULT_EXAMPLE)
                });
            });
        }
        */

    messageArray.push({
      role: "user",
      content: [
        {
          type: "text",
          text: "CRITERIA:\n" + retEventFlag
        },
        {
          type: "data",
          mime_type: "image/jpeg",
          data: imageData.replace("data:image/jpeg;base64,", "")
        }
      ]
    });

    const modelId = getModelId(getState(), id);
    const model = aiServiceReducer.getModelById(getState(), modelId);

    const genConfig = {
      type: "generate_content",
      generation_config: {
        model_id: model?.id,
        model: model?.external_id,
        max_tokens: 1500
      },
      use_case_name: "LLM",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: messageArray
    };

    console.log("what is genConfig", genConfig, IMAGE_PROMPT, JSON.stringify(genConfig));

    dispatch(
      addMessage(id, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        text: IMAGE_PROMPT,
        image: imageData,
        date: new moment()
      })
    );
    dispatch(tagsReducer.updateActiveCapability(0));
    if (template?.isGuardrailsEnabled) {
      const questionIndex = dispatch(
        addMessage(id, {
          user: "LLM",
          date: new moment(),
          isMultiStep: "guardrails",
          useGuardrails: true
        })
      );
      dispatch(tagsReducer.updateActiveCapability(1));
      let imageTestResult;
      try {
        imageTestResult = await dispatch(chatGPTReducer.doCompletion(genConfig));
      } catch (e) {
        console.log("Image test failed: ", e);
        dispatch(handleAgentError(e, template?.id, undefined, undefined, questionIndex));
      }

      try {
        const fixJSON = (imageTestResult?.data?.output?.content ?? "").replace("```json", "").replace("```", "");
        const parseResult = JSON.parse(fixJSON);

        dispatch(
          addMultiStepMessage(id, questionIndex, {
            user: "LLM",
            text: parseResult?.reason,
            result: parseResult?.result,
            description: parseResult?.description,
            date: new moment()
          })
        );

        await dispatch(handleGuardrails(id, imageTestResult?.data?.output?.content, false, questionIndex));

        dispatch(
          addMessage(id, {
            user: "LLM",
            fewShotImage: imageData,
            date: new moment()
          })
        );
      } catch (e) {
        console.log("Error parsing result", e);

        dispatch(
          addMultiStepMessage(id, questionIndex, {
            user: "LLM",
            text: imageTestResult?.data?.output?.content,
            date: new moment()
          })
        );

        await dispatch(handleGuardrails(id, imageTestResult?.data?.output?.content, false, questionIndex));

        dispatch(
          addMessage(id, {
            user: "LLM",
            fewShotImage: imageData,
            date: new moment()
          })
        );
      }

      console.log("what is imageTestResult", imageTestResult, imageTestResult?.data?.output?.content);
      const activeCapability = tagsReducer.getActiveCapability(getState());
      dispatch(tagsReducer.updateActiveCapability(activeCapability + 1));
      dispatch(viewReducer.updateTemplateSetupTab("Try"));
      return Promise.resolve();
    }

    dispatch(tagsReducer.updateActiveCapability(1));
    let imageTestResult;
    try {
      imageTestResult = await dispatch(chatGPTReducer.doCompletion(genConfig));
    } catch (e) {
      console.log("Image test failed: ", e);
      dispatch(handleAgentError(e, id, undefined, undefined, undefined));
    }

    try {
      const fixJSON = (imageTestResult?.data?.output?.content ?? "").replace("```json", "").replace("```", "");
      const parseResult = JSON.parse(fixJSON);

      dispatch(
        addMessage(id, {
          user: "LLM",
          text: parseResult?.reason,
          result: parseResult?.result,
          description: parseResult?.description,
          date: new moment()
        })
      );

      dispatch(
        addMessage(id, {
          user: "LLM",
          fewShotImage: imageData,
          date: new moment()
        })
      );
    } catch (e) {
      console.log("Error parsing result", e);

      dispatch(
        addMessage(id, {
          user: "LLM",
          text: imageTestResult?.data?.output?.content,
          date: new moment()
        })
      );

      dispatch(
        addMessage(id, {
          user: "LLM",
          fewShotImage: imageData,
          date: new moment()
        })
      );
    }

    console.log("what is imageTestResult", imageTestResult, imageTestResult?.data?.output?.content);
    const activeCapability = tagsReducer.getActiveCapability(getState());
    dispatch(tagsReducer.updateActiveCapability(activeCapability + 1));
    dispatch(viewReducer.updateTemplateSetupTab("Try"));
    return Promise.resolve();
  };
}

export function uploadCatalogImage(templateId, imageBase64) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), templateId);

    const templateImageFlow = getAiBlueprintImageFlow(getState(), templateId);
    const templateLocale = getAiBlueprintLocale(getState(), templateId);
    const templateBrandingInstructions = getAiBlueprintBrandingInstructions(getState(), templateId);

    dispatch(chatGPTReducer.updateTemplateIsAsking(true));
    dispatch(tagsReducer.updateActiveCapability(0));
    // Generate payload for first step where service will take an image, branding, locale and generate metadata

    const dataGenerationData = {
      locale: templateLocale,
      brand_instructions: templateBrandingInstructions
    };

    const modelId = getModelId(getState(), templateId);
    const model = aiServiceReducer.getModelById(getState(), modelId);

    // console.log("what is modelid, model", modelId, model);

    // Using hardcoded connection_id for now
    const testConnection = {
      default: 392, // First iteration
      salesTest: 401
    };

    const dataGenerationPayload = {
      type: "nvidia_blueprint",
      generation_config: {
        ...chatGPTReducer.getGenerationConfig(getState()),
        presence_penalty: 0,
        model: "9f7b0942-4d51-4152-9b02-980e3f9b4dce"
      },
      use_case_name: "CHAT",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: [],
      display_messages: [],
      blueprint_config: {
        name: "retail_catalog_enrichment",
        step: "vlm_analyze",
        connection_id: testConnection.salesTest,
        payload: {
          files: {
            images: [
              {
                base64: imageBase64,
                filename: template?.name
              }
            ]
          },
          data: dataGenerationData
        }
      }
    };

    // console.log("what is dataGenerationPayload", dataGenerationPayload);

    dispatch(
      addMessage(templateId, {
        user: "User",
        userInitials: loginReducer.getUserInitials(getState()),
        text: templateBrandingInstructions,
        image: imageBase64,
        date: new moment()
      })
    );

    let dataGenerationResult;
    // Simulated delay on changing active capability from evaluating to generating data
    setTimeout(() => {
      dispatch(tagsReducer.updateActiveCapability(1));
    }, 4000);

    try {
      dataGenerationResult = await dispatch(chatGPTReducer.doCompletion(dataGenerationPayload));
      // console.log("dataGenerationResult: ", dataGenerationResult);
    } catch (e) {
      console.log("Image Analysis and data generation failed", e);
      // console.log("dataGenerationResult: ", dataGenerationResult);
      dispatch(handleAgentError(e, template?.id, undefined, undefined));
    }

    const dataGenerationResultContent = dataGenerationResult?.data?.output?.content;
    let retDataGenerationResult = dataGenerationResultContent;
    try {
      const parsedDataGenerationResult = JSON.parse(dataGenerationResultContent);
      if (parsedDataGenerationResult) {
        retDataGenerationResult = parsedDataGenerationResult;
      }
    } catch {
      console.log("Could not parse data generation result");
    }

    const formattedCategories = retDataGenerationResult?.categories?.join(", ");
    const formattedTags = retDataGenerationResult?.tags?.join(", ");
    const formattedColors = retDataGenerationResult?.colors?.join(", ");

    const formattedDataGenerationText = `Title: ${retDataGenerationResult?.title}
    Description: ${retDataGenerationResult?.description}
    Categories: ${formattedCategories}
    Tags: ${formattedTags}
    Colors: ${formattedColors}
    Locale: ${retDataGenerationResult?.locale}`;

    dispatch(
      addMessage(templateId, {
        user: "LLM",
        text: formattedDataGenerationText,
        date: new moment()
      })
    );

    // Prepare for image generation after the metadata generation
    dispatch(tagsReducer.updateActiveCapability(2));
    const imageGenerationData = {
      title: retDataGenerationResult?.title,
      description: retDataGenerationResult?.description,
      categories: retDataGenerationResult?.categories ?? [],
      tags: retDataGenerationResult?.tags ?? [],
      colors: retDataGenerationResult?.colors ?? [],
      locale: retDataGenerationResult?.locale
    };

    // Using hardcoded connection_id for now
    const imageGenerationPayload = {
      type: "nvidia_blueprint",
      generation_config: {
        ...chatGPTReducer.getGenerationConfig(getState()),
        presence_penalty: 0,
        model: "9f7b0942-4d51-4152-9b02-980e3f9b4dce"
      },
      use_case_name: "CHAT",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: [],
      display_messages: [],
      blueprint_config: {
        name: "retail_catalog_enrichment",
        step: "generate_variation",
        connection_id: testConnection.salesTest,
        product_id: retDataGenerationResult?.product_id,
        payload: {
          files: {
            images: [
              {
                base64: imageBase64,
                filename: template?.name
              }
            ]
          },
          data: imageGenerationData
        }
      }
    };

    // console.log("what is imageGenerationPayload", imageGenerationPayload);
    let imageGenerationResult;
    try {
      imageGenerationResult = await dispatch(chatGPTReducer.doCompletion(imageGenerationPayload));
      // console.log("imageGenerationResult: ", imageGenerationResult);
    } catch (e) {
      console.log("Image Generation failed: ", e);
      // console.log("imageGenerationResult: ", imageGenerationResult);
      dispatch(handleAgentError(e, template?.id, undefined, undefined));
    }

    const imageGenerationResultContent = imageGenerationResult?.data?.output?.content;

    try {
      const parseResult = JSON.parse(imageGenerationResultContent);
      const signedUrls = parseResult?.signed_urls;
      const imageGenResult = signedUrls?.["generate_variation.png"];

      const imageDownload = await dispatch(fetchPresignedUrlImage(imageGenResult));

      const imageGenerationResultMessage = {
        user: "LLM",
        text: "",
        image: imageDownload,
        date: new moment()
      };

      dispatch(addMessage(templateId, imageGenerationResultMessage));
    } catch (e) {
      console.log("Error parsing image generation result", e);

      const retMessage = {
        user: "LLM",
        text: imageGenerationResultContent
      };

      dispatch(addMessage(templateId, retMessage));
    }

    dispatch(tagsReducer.updateActiveCapability(3));
    // Move on to 3d Model Generation

    // Using hardcoded connection_id for now
    const modelGenerationPayload = {
      type: "nvidia_blueprint",
      generation_config: {
        ...chatGPTReducer.getGenerationConfig(getState()),
        presence_penalty: 0,
        model: "9f7b0942-4d51-4152-9b02-980e3f9b4dce"
      },
      use_case_name: "CHAT",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: [],
      display_messages: [],
      blueprint_config: {
        name: "retail_catalog_enrichment",
        step: "generate_3d",
        connection_id: testConnection.salesTest,
        product_id: retDataGenerationResult?.product_id,
        payload: {
          files: {
            images: [
              {
                base64: imageBase64,
                filename: template?.name
              }
            ]
          }
        }
      }
    };

    // console.log("what is modelGenerationPayload", modelGenerationPayload);
    let modelGenerationResult;
    try {
      modelGenerationResult = await dispatch(chatGPTReducer.doCompletion(modelGenerationPayload));
      // console.log("modelGenerationResult: ", modelGenerationResult);
    } catch (e) {
      console.log("3D Model Generation failed: ", e);
      // console.log("modelGenerationResult: ", modelGenerationResult);
      dispatch(handleAgentError(e, template?.id, undefined, undefined));
    }

    const modelGenerationResultContent = modelGenerationResult?.data?.output?.content;

    try {
      const parseResult = JSON.parse(modelGenerationResultContent);
      const modelGenerationResultMessage = {
        user: "LLM",
        text: "3D Model has been created and updated in your catalog",
        date: new moment()
      };

      dispatch(addMessage(templateId, modelGenerationResultMessage));
    } catch (e) {
      console.log("Error parsing 3d model generation result", e);

      const retMessage = {
        user: "LLM",
        text: imageGenerationResultContent
      };

      dispatch(addMessage(templateId, retMessage));
    }

    dispatch(tagsReducer.updateActiveCapability(undefined));
    dispatch(viewReducer.updateTemplateSetupTab("Try"));
    dispatch(chatGPTReducer.updateTemplateIsAsking(false));
    return Promise.resolve();
  };
}

export function fetchPresignedUrlImage(presignedUrl, mimeType = "image/png", fileName = "image.png") {
  return async (dispatch, getState) => {
    const response = await fetch(presignedUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();
    const typedBlob = new Blob([blob], { type: mimeType });

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(typedBlob);
    });
  };
}

export function handleGuardrails(
  id,
  prevResponse,
  isLocal = true,
  questionIndex = undefined,
  isReasoning = false,
  isCached = false
) {
  return async (dispatch, getState) => {
    const template = getTemplate(getState(), id);
    const activeCapability = tagsReducer.getActiveCapability(getState());
    dispatch(tagsReducer.updateActiveCapability(activeCapability + 1));

    const modelId = getModelId(getState(), id);

    const secondaryTextModelId = getSecondaryTextModelId(getState(), id) ?? modelId;
    let secondaryTextModel = aiServiceReducer.getModelById(getState(), secondaryTextModelId);
    if (!secondaryTextModel) {
      secondaryTextModel = aiServiceReducer.getModelById(getState(), modelId);
    }

    const genConfig = {
      type: "generate_content",
      generation_config: !isLocal
        ? {
            model_id: secondaryTextModel?.id,
            model: secondaryTextModel?.external_id,
            max_tokens: 400
          }
        : {
            model_id: secondaryTextModel?.id,
            model: secondaryTextModel?.external_id,
            max_tokens: 400
          },
      use_case_name: "LLM",
      folder_id: folderReducer.getSelectedFolder(getState()),
      messages: [
        {
          role: "system",
          content: template?.guardrailInstructions
        },
        {
          role: "user",
          content: prevResponse
        }
      ]
    };

    if (!questionIndex) {
      dispatch(
        addMessage(id, {
          user: "User",
          userInitials: loginReducer.getUserInitials(getState()),
          title: "Evaluate Bias Guardrails",
          text: template?.guardrailInstructions + "\n\n" + prevResponse,
          hasDetails: true
        })
      );
    }
    let guardrailsResponse;
    try {
      guardrailsResponse = await dispatch(chatGPTReducer.doCompletion(genConfig));
    } catch (e) {
      console.log("Guardrails check failed: ", e);
      dispatch(handleAgentError(e, template?.id, undefined, undefined, questionIndex));
    }

    console.log("guardrailsResponse", guardrailsResponse);
    try {
      const fixJSON = guardrailsResponse?.data?.output?.content?.replace("```json", "").replace("```", "");
      const parsedResponseOutput = JSON.parse(fixJSON);
      console.log("Parsed response output: ", parsedResponseOutput);
      const biasResult = parsedResponseOutput?.result?.toLowerCase() === "yes";

      if (questionIndex) {
        dispatch(
          addMultiStepMessage(id, questionIndex, {
            user: "LLM",
            text: parsedResponseOutput?.result + "\n\n" + parsedResponseOutput?.reason,
            hasBias: biasResult
          })
        );
        const template = getTemplate(getState(), id);
        const retMessage = {
          ...template?.messages[questionIndex],
          text: isReasoning ? "" : prevResponse
        };
        if (isCached) {
          retMessage.isCachedResponse = true;
        }
        return dispatch(updateMessage(id, questionIndex, retMessage));
      }

      dispatch(
        addMessage(id, {
          user: "LLM",
          text: parsedResponseOutput?.result + "\n\n" + parsedResponseOutput?.reason,
          hasBias: biasResult
        })
      );
    } catch (e) {
      console.log("Error parsing result", e);

      if (questionIndex) {
        dispatch(
          addMultiStepMessage(id, questionIndex, {
            user: "LLM",
            text: guardrailsResponse?.data?.output?.content
          })
        );

        const template = getTemplate(getState(), id);
        const retMessage = {
          ...template?.messages[questionIndex],
          text: "Error parsing guardrails response"
        };
        return dispatch(updateMessage(id, questionIndex, retMessage));
      }

      dispatch(
        addMessage(id, {
          user: "LLM",
          text: guardrailsResponse?.data?.output?.content
        })
      );
    }
  };
}

export function resetToDefault(templateId) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), templateId);
    const baseTemplate =
      template?.media === "audio"
        ? "Audio Analysis"
        : template?.media === "image"
          ? "Image Analysis"
          : template?.media === "image_reasoning"
            ? "Image Analysis (Reasoning)"
            : "Video Analysis";

    dispatch(tagsReducer.updateActiveCapability(undefined));
    return dispatch(updateTemplate(templateId, initialState?.[baseTemplate]));
  };
}

export function getLineage(state, templateId) {
  const template = getTemplate(state, templateId);

  const templateBlueprint = template?.blueprintId
    ? blueprintReducer.getBlueprint(state, template.blueprintId)
    : undefined;

  const rawLineage = template?.lineage ?? [];

  let previousBpValues;
  const lineage = rawLineage?.map((lineageItem) => {
    const lineageTemplate = getTemplate(state, lineageItem?.id);
    const lineageBp = lineageTemplate?.blueprintId
      ? blueprintReducer.getBlueprint(state, lineageTemplate?.blueprintId)
      : undefined;

    const { wizard, ...bpDetails } = lineageBp ?? {};

    const lineageBpValues = {
      ...(wizard ?? {}),
      ...lineageTemplate,
      ...(bpDetails ?? {})
    };

    const updatedDate = lineageBp ? lineageBp.updatedDate : lineageTemplate?.updatedDate;

    let changes;
    if (previousBpValues) {
      changes = chatAppReducer.getLineageChanges(state, previousBpValues, lineageBpValues, TEMPLATE_LINEAGE_CHANGES);
    }
    previousBpValues = lineageBpValues;

    return {
      ...lineageItem,
      ...lineageBpValues,
      lineageChanges: changes,
      isUpdated: lineageItem?.updated_date !== updatedDate
    };
  });

  const { wizard, ...bpDetails } = templateBlueprint ?? {};
  const currLineageItem = {
    ...(wizard ?? {}),
    ...template,
    ...(bpDetails ?? {})
  };
  if (previousBpValues) {
    currLineageItem.lineageChanges = chatAppReducer.getLineageChanges(
      state,
      previousBpValues,
      currLineageItem,
      TEMPLATE_LINEAGE_CHANGES
    );
  }

  lineage.push(currLineageItem);

  return lineage;
}

export function saveTemplate(
  templateId,
  name,
  skipSaveMessages = false,
  saveToMyWork = false,
  openAgentOnSave = false,
  saveLineage = true
) {
  return (dispatch, getState) => {
    const template = getTemplate(getState(), templateId);

    console.log("what is template, templateId", template, templateId);

    const retTemplate = {
      wizard: {
        template: {
          ...template
        }
      },
      name: name || template?.name
    };

    // Currently do continue saving these images and text so when the wizard is finished and redirected,
    // info is still availablewhen the wizard is re-opened.
    //
    // if (retTemplate?.wizard?.template?.introText) {
    //     delete retTemplate.wizard.template.introText;
    // }

    // if (retTemplate?.wizard?.template?.introExamples) {
    //     delete retTemplate.wizard.template.introExamples;
    // }

    if (saveToMyWork && isEnabled("chat-aible-templates")) {
      retTemplate.wizard.template.wizard = {
        ...retTemplate.wizard.template.wizard,
        isSavedWork: true
      };
    }

    if (skipSaveMessages && retTemplate?.wizard?.template?.messages) {
      retTemplate.wizard.template.messages = retTemplate.wizard.template.messages.slice(0, 1);
      retTemplate.wizard.template.messages[0].date = new moment();
      if (retTemplate?.wizard?.template?.images) {
        retTemplate.wizard.template.images = {};
        retTemplate.wizard.template.imageCount = 0;
      }
    }

    let currLineage = [];
    if (saveLineage) {
      let parentTemplate = template;
      if (template?.blueprintId) {
        parentTemplate = blueprintReducer.getBlueprint(getState(), template?.blueprintId);
      }
      const templateLineage = parentTemplate?.wizard?.template?.lineage ?? [];

      const currLineageItem = {};
      const KEYS_TO_SAVE = ["created_by", "created_date", "updated_date", "id", "name"];
      KEYS_TO_SAVE.forEach((k) => {
        currLineageItem[k] = parentTemplate[k];
      });
      currLineage = [...templateLineage, currLineageItem];
    }
    retTemplate.wizard.template.lineage = currLineage;

    console.log("save template", retTemplate);

    dispatch(updateIsSavingTemplate(templateId, true));

    try {
      return dispatch(
        blueprintReducer.createBlueprintFromBlueprint(retTemplate, undefined, openAgentOnSave, saveToMyWork)
      ).then((response) => {
        dispatch(fetchTemplates());
        dispatch(updateIsSavingTemplate(templateId, false));
        return response;
      });
    } catch (e) {
      console.log("error savinvg template", e);

      dispatch(updateIsSavingTemplate(templateId, false));
    }
  };
}

export function fetchTemplates(isClear = false) {
  return (dispatch, getState) => {
    let retDefaultTemplates = initialState;

    if (isEnabled("ai-blueprint-templates") && !isEnabled("spark-agents-templates-view")) {
      retDefaultTemplates = {
        [TEMPLATE_AI_BLUEPRINT_RETAIL_CATALOG?.id]: { ...TEMPLATE_AI_BLUEPRINT_RETAIL_CATALOG },
        ...DEFAULT_TEMPLATES
      };
    }

    let templateBase = { ...(isClear === true ? retDefaultTemplates : getBase(getState())) };
    const blueprints = blueprintReducer.getBlueprints(getState());

    blueprints
      .filter((bp) => bp?.wizard?.template !== undefined)
      .forEach((bp) => {
        const retBlueprint = {
          ...bp.wizard.template,
          id: bp?.id,
          blueprintId: bp?.id,
          itemType: "Agent"
        };
        if (!retBlueprint?.media) {
          if (retBlueprint?.audio) {
            retBlueprint.media = "audio";
          } else if (retBlueprint?.isLocalModel) {
            retBlueprint.media = "image";
          } else {
            retBlueprint.media = "video";
          }
        }
        templateBase[bp.id] = retBlueprint;
      });

    dispatch(updateBase(templateBase));

    console.log("what is templateBase blueprints", templateBase, blueprints);
  };
}

export function handleAgentError(
  error,
  templateId,
  isDontAddMsg = false,
  errorMsgOverride = undefined,
  questionIndex = undefined
) {
  return (dispatch, getState) => {
    console.log("agent error");
    console.log(error);
    console.log(error?.response ?? error?.message);
    const templateIsAsking = chatGPTReducer.getTemplateIsAsking(getState());

    let errorText = errorMsgOverride ?? DEFAULT_ERROR_MESSAGE;

    let errorType = error?.cause?.type ?? error?.cause;

    if (errorType === "MODEL_UNAVAILABLE") {
      errorText = MODEL_UNAVAILABLE_ERROR_MESSAGE;
    }

    if (errorType === "TOKEN_LIMIT") {
      errorText = TOKEN_LIMIT_ERROR_MESSAGE;
    }

    if (errorType === "CONTENT_FILTERED") {
      errorText = CONTENT_FILTERED_ERROR_MESSAGE;
    }

    if (errorType === "SQL_PARSE") {
      errorText = SQL_PARSE_ERROR_MESSAGE;
    }

    if (errorType === "SQL_VALIDATION") {
      errorText = SQL_VALIDATION_ERROR_MESSAGE;
    }

    if (errorType === "SQL_EXECUTION") {
      errorText = SQL_EXECUTION_ERROR_MESSAGE;
    }

    if (errorType === "NO_DOCUMENTS") {
      errorText = DOCUMENT_SET_HAS_NO_DOCUMENTS_MESSAGE;
    }

    let advancedMessage = error?.response?.data?.error?.message || error?.message?.[0]?.message || error?.message;

    if (isEnabled("chat-aible-consumer") && !isEnabled("chat-aible-advanced-controls")) {
      advancedMessage = undefined;
    }

    const accountId = chatGPTReducer.getChatGPTAccountId(getState());

    let id = error?.cause?.id;

    if (isDontAddMsg !== true) {
      if (questionIndex !== undefined) {
        dispatch(
          addMultiStepMessage(templateId, questionIndex, {
            user: "LLM",
            text: errorText,
            date: new moment(),
            accountId: accountId,
            isError: true,
            ids: id ? [id] : undefined,
            errorMessage: advancedMessage
          })
        );
        const template = getTemplate(getState(), templateId);
        const retMessage = {
          ...template?.messages[questionIndex],
          isError: true,
          text: errorText,
          ids: id ? [id] : undefined,
          errorMessage: advancedMessage
        };
        dispatch(updateMessage(templateId, questionIndex, retMessage));
        dispatch(viewReducer.updateTemplateSetupTab("Try"));
        if (templateIsAsking) {
          dispatch(chatGPTReducer.updateTemplateIsAsking(false));
        }
        throw error;
      }
      dispatch(
        addMessage(templateId, {
          user: "LLM",
          text: errorText,
          date: new moment(),
          accountId: accountId,
          isError: true,
          ids: id ? [id] : undefined,
          errorMessage: advancedMessage
        })
      );
      dispatch(viewReducer.updateTemplateSetupTab("Try"));
      if (templateIsAsking) {
        dispatch(chatGPTReducer.updateTemplateIsAsking(false));
      }
      throw error;
    }
  };
}

export default templateReducer;
